import React, { useState, useEffect } from "react";
import { useAuth } from "../lib/AuthContext";
import { motion, AnimatePresence } from "motion/react";
import {
  Users,
  LayoutGrid,
  Sparkles,
  Zap,
  Workflow,
  Archive,
  Calendar,
  TrendingUp,
  CreditCard,
  MessageSquare,
  ShieldAlert,
  Send,
  Plus,
  Check,
  X,
  Download,
  ExternalLink,
  Eye,
  Sliders,
  CheckCircle2,
  AlertCircle,
  FileText,
  Clock,
  Trash2,
  RefreshCw,
  Info,
  Share2,
  ArrowUpRight
} from "lucide-react";

interface ClientCampaignWorkspaceProps {
  triggerToast: (msg: string, type?: any) => void;
  userTier?: string;
  initialTab?: "dashboard" | "request" | "brand" | "approval" | "timeline" | "assets" | "calendar" | "performance" | "billing" | "support" | "agency" | "superadmin";
}

export function ClientCampaignWorkspace({ triggerToast, userTier = "Bronze", initialTab = "dashboard" }: ClientCampaignWorkspaceProps) {
  const { user } = useAuth();
  const isDirector = user?.email?.toLowerCase() === "siddique.mahmood@gmail.com";

  // Real Campaign Launcher States
  const CAMPAIGN_SEEDS: Record<string, {
    name: string;
    routeType: string;
    destination: string;
    facebookCopy: string;
    linkedinCopy: string;
    whatsappCopy: string;
    emailCopy: string;
  }> = {
    "DRIVER_FLEET_LAUNCH": {
      name: "Driver / Courier / Fleet Command System",
      routeType: "Request Quote",
      destination: "https://nexoraxdigital.co.uk/pricing?utm_source=campaign&utm_medium=manual&utm_campaign=driver_fleet_quote#driver-fleet-quote",
      facebookCopy: "💥 Stop paying massive commissions to third-party delivery apps. Build and run your own independent driver fleet using the NexoraX Command System. Track couriers in real-time, manage dynamic pricing, and own your local market. Get started today: https://nexoraxdigital.co.uk",
      linkedinCopy: "🚀 Streamline your logistics infrastructure, reduce dispatch overhead, and scale B2B operations with the NexoraX Fleet Command System. A premium, high-availability dispatch management center for couriers, transport fleets, and delivery networks. Request your custom enterprise proposal at https://nexoraxdigital.co.uk",
      whatsappCopy: "Launch your independent delivery fleet with NexoraX Fleet Command. Real-time tracking & custom pricing. Details: https://nexoraxdigital.co.uk",
      emailCopy: `Subject: [TEST] NexoraX Campaign: Driver / Courier / Fleet System Launch

Dear Director,

This is a NexoraX campaign test for the Courier & Fleet Dispatch Command System. Our lead capture routing (POST /api/launch-list) and SQLite Task Matrix queue are fully operational.

Campaign Details:
- Product: Courier & Fleet Command System
- Route: Request Quote
- Destination: https://nexoraxdigital.co.uk/pricing?utm_source=campaign&utm_medium=manual&utm_campaign=driver_fleet_quote#driver-fleet-quote

Public Contact Details:
NexoraX Digital LTD
nexoraxdigital.co.uk
info@nexoraxdigital.co.uk
support@nexoraxdigital.co.uk
Freephone: 08000 622827

Build Faster. Scale Smarter. Future-Ready.`
    },
    "WEBSITE_PACKAGES_LAUNCH": {
      name: "Website Launch Packages",
      routeType: "Direct Payment",
      destination: "https://nexoraxdigital.co.uk/pricing?utm_source=campaign&utm_medium=manual&utm_campaign=web_launch_packages",
      facebookCopy: "✨ Need a website that actually converts? Get a stunning, ultra-fast website built by NexoraX Digital LTD. Obsidian glass designs, neon accents, and zero conversion lag. Secure checkout: https://nexoraxdigital.co.uk",
      linkedinCopy: "🚀 Elevate your brand presence with our custom Web Launch Packages. Engineered for high-end B2B performance with full responsive layouts and interactive dashboards. Secure checkout link: https://nexoraxdigital.co.uk/pricing",
      whatsappCopy: "Get a premium, ultra-fast B2B website designed by NexoraX. View packages: https://nexoraxdigital.co.uk",
      emailCopy: `Subject: [TEST] NexoraX Campaign: Website Launch Packages

Dear Director,

This is a NexoraX campaign test for the Website Launch Packages. Our secure payment checkouts (Bronze, Silver, Gold tiers) are fully integrated.

Campaign Details:
- Product: Website Launch Packages
- Route: Direct Payment (Deposits)
- Destination: https://nexoraxdigital.co.uk/pricing

Public Contact Details:
NexoraX Digital LTD
nexoraxdigital.co.uk
info@nexoraxdigital.co.uk
support@nexoraxdigital.co.uk
Freephone: 08000 622827

Build Faster. Scale Smarter. Future-Ready.`
    },
    "MAINTENANCE_HOSTING_LAUNCH": {
      name: "Hosting & Maintenance Packages",
      routeType: "Maintenance Payment",
      destination: "https://nexoraxdigital.co.uk/pricing?utm_source=campaign&utm_medium=manual&utm_campaign=maintenance_hosting",
      facebookCopy: "🛠 Keep your website secure and online. Our NexoraX Maintenance plans include 24/7 monitoring, daily backups, and regular server patches on secure cloud hosting. Secure checkout: https://nexoraxdigital.co.uk",
      linkedinCopy: "🚀 High-availability cloud hosting and security maintenance for B2B enterprises. Protect your digital assets, eliminate vulnerabilities, and ensure 99.99% uptime with NexoraX. Secure checkout link: https://nexoraxdigital.co.uk/pricing",
      whatsappCopy: "Secure your website node with NexoraX Hosting & 24/7 Maintenance. Plans: https://nexoraxdigital.co.uk",
      emailCopy: `Subject: [TEST] NexoraX Campaign: Hosting & Maintenance Packages

Dear Director,

This is a NexoraX campaign test for Hosting, Mail & Maintenance subscriptions. Our recurring checkouts (Starter, Premier, Pro) are active.

Campaign Details:
- Product: Hosting & Maintenance Plans
- Route: Maintenance Payment (Subscriptions)
- Destination: https://nexoraxdigital.co.uk/pricing

Public Contact Details:
NexoraX Digital LTD
nexoraxdigital.co.uk
info@nexoraxdigital.co.uk
support@nexoraxdigital.co.uk
Freephone: 08000 622827

Build Faster. Scale Smarter. Future-Ready.`
    }
  };

  const [realCampaigns, setRealCampaigns] = useState<any[]>([]);
  const [selectedCampId, setSelectedCampId] = useState<string>("DRIVER_FLEET_LAUNCH");
  const [campImages, setCampImages] = useState<string[]>([]);
  const [campQRs, setCampQRs] = useState<string[]>([]);
  const [selectedImage, setSelectedImage] = useState<string>("futuristic_fleet_dispatch_system_ad.png");
  const [selectedQR, setSelectedQR] = useState<string>("");
  const [emailStatus, setEmailStatus] = useState<string>("Configured / Test Ready");
  const [taskQueueStatus, setTaskQueueStatus] = useState<string>("idle");
  const [sendingTestEmail, setSendingTestEmail] = useState<boolean>(false);
  const [testEmailSentThisSession, setTestEmailSentThisSession] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);

  useEffect(() => {
    fetch("/api/campaign-assets")
      .then(res => res.json())
      .then(data => {
        if (data.success && data.campaigns && data.campaigns.length > 0) {
          const filtered = data.campaigns.filter((c: any) => c.id !== "ALL_CAMPAIGN_IMAGES" && c.id !== "ALL_QR_CODES");
          setRealCampaigns(filtered);
        }
      })
      .catch(err => console.error("Error loading campaigns:", err));
  }, []);

  useEffect(() => {
    if (selectedCampId && realCampaigns.length > 0) {
      const currentCamp = realCampaigns.find(c => c.id === selectedCampId);
      if (currentCamp) {
        const imgs = currentCamp.files.filter((f: string) => f.endsWith(".png") || f.endsWith(".jpg") || f.endsWith(".jpeg") || f.endsWith(".webp"));
        const qrs = currentCamp.files.filter((f: string) => f.toLowerCase().includes("qr") || f.toLowerCase().includes("code") || f.toLowerCase().includes("redirect"));
        setCampImages(imgs);
        setCampQRs(qrs);
        
        const defaultImg = imgs.find((f: string) => f === "futuristic_fleet_dispatch_system_ad.png") || imgs.find((f: string) => f === "futuristic_web_design_promo_ad.png") || imgs.find((f: string) => f === "futuristic_hosting_and_maintenance_services.png") || imgs[0] || "";
        setSelectedImage(defaultImg);
        setSelectedQR(qrs[0] || "");
      }
    }
  }, [selectedCampId, realCampaigns]);

  const activeSeed = CAMPAIGN_SEEDS[selectedCampId] || CAMPAIGN_SEEDS["DRIVER_FLEET_LAUNCH"];

  const handleSendTestEmail = async () => {
    if (testEmailSentThisSession) {
      triggerToast("Test email already sent this session.", "info");
      return;
    }
    setSendingTestEmail(true);
    try {
      const res = await fetch("/api/revenue/email/test-send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subject: activeSeed.name + " Outreach Pitch",
          body: activeSeed.emailCopy
        })
      });
      const data = await res.json();
      if (data.success) {
        setTestEmailSentThisSession(true);
        setEmailStatus("TEST SENT TO ADMIN");
        triggerToast("Controlled Campaign Test Email Sent!", "success");
      } else {
        const errMsg = data.error || "Unknown Error";
        if (errMsg.toLowerCase().includes("not configured")) {
          setEmailStatus("NOT CONFIGURED");
        } else {
          setEmailStatus("FAILED");
        }
        triggerToast("Email test send failed: " + errMsg, "error");
      }
    } catch (err: any) {
      const errMsg = err.message || String(err);
      if (errMsg.toLowerCase().includes("not configured")) {
        setEmailStatus("NOT CONFIGURED");
      } else {
        setEmailStatus("FAILED");
      }
      triggerToast("Error dispatching test email", "error");
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleQueueTask = async () => {
    setTaskQueueStatus("queuing");
    try {
      const res = await fetch("/api/campaign-assets/queue-task", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: `[CAMPAIGN READY] ${activeSeed.name} Launch`,
          priority: "HIGH",
          category: "Marketing / Revenue",
          description: `Campaign assets are verified and staged. Target destination: ${activeSeed.destination}. Requires manual post approval and sharing on Facebook, LinkedIn, WhatsApp.`
        })
      });
      const data = await res.json();
      if (data.success) {
        setTaskQueueStatus("queued");
        triggerToast("Follow-up task created in SQLite backlog!", "success");
      } else {
        setTaskQueueStatus("failed");
        triggerToast("Failed to queue campaign task", "error");
      }
    } catch (err) {
      setTaskQueueStatus("failed");
      triggerToast("Error queuing campaign task", "error");
    }
  };

  // --- STATE STUFF ---
  const [role, setRole] = useState<"Client" | "Agency/Admin" | "Super Admin">("Client");
  const isAllowedToSwitchRoles = role !== "Client" || isDirector;
  const [activeTab, setActiveTab] = useState<
    "dashboard" | "request" | "brand" | "approval" | "timeline" | "assets" | "calendar" | "performance" | "billing" | "support" | "agency" | "superadmin"
  >(initialTab);

  // Client comments edit state map
  const [clientCommentsInput, setClientCommentsInput] = useState<Record<string, string>>({
    "APP-001": "",
    "APP-002": "",
    "APP-003": ""
  });

  // Stateful admin notes & documents
  const [adminNotes, setAdminNotes] = useState(
    "Eleanor Vance campaign has high click-through yield. Core focus on Client Resource Workflow offices. All parameters conform to standard Resource deployment criteria. Keep GPU limits optimized to prevent replication drifts."
  );

  // KPI States with synchronized LocalStorage hooks
  const [activeCampaigns, setActiveCampaigns] = useState(6);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(12);
  const [videosGenerated, setVideosGenerated] = useState(48);
  const [postsScheduled, setPostsScheduled] = useState(120);
  const [leadsGenerated, setLeadsGenerated] = useState(1240);
  const [monthlySpend, setMonthlySpend] = useState(5100);
  const [roiForecast, setRoiForecast] = useState("£18.4K");

  // 1. Dashboard State
  const [subscriptionPlan, setSubscriptionPlan] = useState("Pro Resource NexoraX Tier");
  const [activeServiceTickets, setActiveServiceTickets] = useState(1);

  // 2. Campaign Requests
  const [campaignGoal, setCampaignGoal] = useState("Brand Awareness & Lead Generation");
  const [campaignTargetAudience, setCampaignTargetAudience] = useState("Modern Tech and Fashion Consumers");
  const [campaignProduct, setCampaignProduct] = useState("Premium SaaS Creative Product Suite");
  const [campaignOffer, setCampaignOffer] = useState("Secure Stripe Integration Demonstration with 20% Discount with Coupon Code CREATIVE20");
  const [campaignPlatform, setCampaignPlatform] = useState("LinkedIn");
  const [campaignBudget, setCampaignBudget] = useState("£5,100");
  const [campaignDeadline, setCampaignDeadline] = useState("2026-07-01");
  const [campaignNotes, setCampaignNotes] = useState("Require high-fidelity horizontal cinematic viewports and optimized 9:16 portrait files.");

  const [campaignHistory, setCampaignHistory] = useState<any[]>([
    {
      id: "REQ-109",
      goal: "UGC Video Promo Campaign",
      audience: "Fashion Enthusiasts",
      product: "Organic Cotton Apparel Reel",
      offer: "20% off Launch Promo with Custom Visuals",
      platform: "LinkedIn / TikTok / Instagram",
      budget: "£2,500",
      deadline: "2026-06-25",
      notes: "NexoraX visual design with smooth e-commerce transitions.",
      status: "Active Strategy",
      dateSubmitted: "2026-06-11"
    },
    {
      id: "REQ-108",
      goal: "Corporate Brand Awareness",
      audience: "Enterprise Partners & Stakeholders",
      product: "Conversion Analytics Dashboard Promo",
      offer: "Secure Stripe Integration Demonstration with zero-friction checkout",
      platform: "YouTube Longform",
      budget: "£12,000",
      deadline: "2026-07-15",
      notes: "Showcase real-time conversion metrics and beautiful charts.",
      status: "Approved",
      dateSubmitted: "2026-06-10"
    }
  ]);

  // 3. Brand Profile State
  const [brandName, setBrandName] = useState("E-Commerce Creative Studio");
  const [brandColors, setBrandColors] = useState("#a855f7, #00e5ff, #090412");
  const [brandTone, setBrandTone] = useState("Modern, Professional, Creative, Trustworthy");
  const [brandUrl, setBrandUrl] = useState("https://yourbrand.com");
  const [brandAudience, setBrandAudience] = useState("E-Commerce Retailers & High-Growth Brands");
  const [brandDetails, setBrandDetails] = useState("Premium automated asset generation, high-contrast video ads, and instant social sharing integrations.");
  const [brandDoRules, setDoRules] = useState("Keep high contrast, use modern sans typography, highlight elegant purple and cyan accent overlays.");
  const [brandDontRules, setDontRules] = useState("Never use low-contrast text. Avoid plain layout structures. Keep branding guidelines strictly consistent.");
  const [brandCtaRules, setCtaRules] = useState("Establish secure Stripe subscriptions, bypass generic workflows, and connect custom channels directly.");
  const [brandExample, setBrandExample] = useState("‘Access your automated media generation dashboard now. Start building with zero conversion delay.’");

  // 4. Approval Center State
  const [approvalItems, setApprovalItems] = useState<any[]>([
    {
      id: "APP-001",
      title: "Eleanor Vance UGC Ad Segment (15s)",
      type: "UGC Video",
      status: "Pending Review",
      platform: "LinkedIn Video Ad",
      version: "v2.4",
      playbackUrl: "https://images.unsplash.com/photo-1557804506-669a67965ba0?q=80&w=3274&auto=format&fit=crop",
      tagline: "Eleanor Vance (94% CTR calibration)",
      comments: "Review speech delivery rate. Ensure the Secure Payment Provider access reference is clearly subtitled.",
      caption: "Our Principal Private Secretaries are fully synchronized to the NexoraX NexoraX Resource resource, routing corporate traffic with absolute speed..."
    },
    {
      id: "APP-002",
      title: "Secure Payment Provider Reconciliation Dashboard Render",
      type: "Social Ad Creative",
      status: "Pending Review",
      platform: "LinkedIn Sponsored Spot",
      version: "v1.2",
      playbackUrl: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=3164&auto=format&fit=crop",
      tagline: "Obisidian Glass Visual Panel",
      comments: "Perfect render resolution. The SecurePayment Sort Code / Account matches the global ledger.",
      caption: "NexoraX Financial Security: Extracting a secure 1% perpetual levy and 10% Maintenance & Hosting Fee directly to SecurePayment vaults. Secure your node today."
    },
    {
      id: "APP-003",
      title: "Resource Launch Press Release Copy v1.2",
      type: "Blog Post / Article",
      status: "Approved",
      platform: "Website / Blog",
      version: "v1.0",
      playbackUrl: "",
      tagline: "High-density Corporate Statement",
      comments: "Excellent structure and zero digital drifts. Ready for direct distribution.",
      caption: "NexoraX Board of Director decrees operational deployment of £150,000 NexoraX resource program under absolute executive directives..."
    }
  ]);

  // 5. Timeline Nodes (Holographic Workflow tracking with interactive milestones)
  const timelineWorkflow = [
    { name: "Request Received", desc: "Formulated payload in Client Vault", date: "June 11, 2026", status: "completed" },
    { name: "Strategic Mapping", desc: "Node-06 Oracle calibration", date: "June 11, 2026", status: "completed" },
    { name: "Synthetic Scripts", desc: "NexoraX Hook extraction active", date: "June 12, 2026", status: "completed" },
    { name: "Creative Framing", desc: "Layer mapping & asset design", date: "June 12, 2026", status: "current" },
    { name: "Video Render", desc: "Veo-Deep 2.4 GPU pipeline render", date: "Pending Review", status: "upcoming" },
    { name: "Approval Desk", desc: "SaaS client authorization gateway", date: "Pending Approval", status: "upcoming" },
    { name: "Platform Scheduler", desc: "Timed buffer queue assembly", date: "Pending Sync", status: "upcoming" },
    { name: "Live Dissemination", desc: "0.8s broadcast to active channels", date: "Queued", status: "upcoming" },
    { name: "CTR Analytics Monitor", desc: "Telemetry loop telemetry sweep", date: "Awaiting Live", status: "upcoming" }
  ];

  // 6. Asset Library
  const [assets, setAssets] = useState<any[]>([
    { id: "AST-8201", type: "Video", name: "eleanor_vance_ugc_master.mp4", size: "34.5 MB", duration: "15s", version: "v2.4", date: "2026-06-12", url: "https://images.unsplash.com/photo-1542751371-adc38448a05e?q=80&w=2670&auto=format&fit=crop" },
    { id: "AST-8202", type: "Image", name: "SecurePayment_ledger_banner_linkedin.png", size: "4.2 MB", duration: "1920x1080", version: "v1.2", date: "2026-06-11", url: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=3164&auto=format&fit=crop" },
    { id: "AST-8203", type: "Doc", name: "Secure Payment Provider_access_to_work_nh448796b.pdf", size: "1.8 MB", duration: "Formal Legal", version: "v1.0", date: "2026-06-08", url: "" },
    { id: "AST-8204", type: "Audio", name: "eleanor_synthetic_voice_model.wav", size: "12.0 MB", duration: "3m 40s", version: "v4.1", date: "2026-06-10", url: "" },
    { id: "AST-8205", type: "JSON", name: "brand_voice_calibration_directives.json", size: "148 KB", duration: "System Map", version: "v9.2", date: "2026-06-11", url: "" }
  ]);

  // 7. Publishing Calendar
  const [calendarEvents, setCalendarEvents] = useState<any[]>([
    { date: "June 13, 2026", platform: "LinkedIn", title: "Unveiling Secure Payment Provider Zero-Cost Client Resource Workflow Scheme", status: "Approved", approvalState: "Ready", time: "09:00 AM" },
    { date: "June 14, 2026", platform: "YouTube Shorts", title: "Eleanor Vance - Secure Invoice Register Productivity Metrics", status: "Awaiting Video", approvalState: "Pending", time: "11:30 AM" },
    { date: "June 15, 2026", platform: "Instagram", title: "NexoraX Obsidian Glassmorphism Design", status: "Approved", approvalState: "Ready", time: "05:00 PM" },
    { date: "June 16, 2026", platform: "LinkedIn", title: "Secure Payment Provider NexoraX Ledger Sweeps Explained", status: "Needs Review", approvalState: "Draft", time: "08:30 AM" },
    { date: "June 17, 2026", platform: "Newsletter", title: "Institutional Client Resource Workflow Redefined (WP-1)", status: "Approved", approvalState: "Ready", time: "10:00 AM" }
  ]);

  // 8. Performance Analytics
  const [performanceData, setPerformanceData] = useState({
    views: "248.5K",
    engagement: "14.22%",
    leads: "1,240",
    ctr: "5.42%",
    conversion: "3.85%",
    bestPlatform: "LinkedIn (94% CTR alignment)",
    bestVideo: "Eleanor Vance Professional v2.4 (15s UGC)",
    bestHook: "‘Save 10 Hours Per Week with Authorized Resource Assets’",
    roiEst: "£18.4K"
  });

  // 9. Billing and Usage State
  const [creditsUsed, setCreditsUsed] = useState(840);
  const [creditsTotal, setCreditsTotal] = useState(1000);
  const [campaignsCreated, setCampaignsCreated] = useState(14);
  const [nextInvoiceDate, setNextInvoiceDate] = useState("June 20, 2026");
  const [pendingBalance, setPendingBalance] = useState("£510.00");

  // 10. Collaboration / Support comments list
  const [supportMessages, setSupportMessages] = useState<any[]>([
    { sender: "Director Mahmood", text: "Please confirm that ALL candidate assets explicitly reference the NexoraX billing records grant under NI: NH******B.", time: "10:14 AM", role: "Manager" },
    { sender: "Eleanor Vance (Client)", text: "I have requested a revision of UGC Ad segment. The subtitles had a subtle spelling drift regarding SC887095 registration detail.", time: "10:30 AM", role: "Client" },
    { sender: "Staff Coordinator (Tech Team)", text: "Copy that. We calibrated the rendering seed with zero digital drift. Eleanor v2.4 contains the corrected licensing overlay.", time: "10:48 AM", role: "Agency/Admin" }
  ]);

  const [newMessage, setNewMessage] = useState("");

  // --- ADDITIONAL PREMIUM STATE ---
  // Bulk Export Status State
  const [exportStatus, setExportStatus] = useState<"idle" | "preparing" | "packaging" | "finalizing" | "completed">("idle");
  const [exportProgress, setExportProgress] = useState(0);
  const [exportTrace, setExportTrace] = useState<string>("");
  const [exportHistory, setExportHistory] = useState<any[]>([
    { id: "EXP-9821", name: "nexorax_linkedin_campaign_bundle.zip", count: 5, date: "2026-06-11", status: "Ready", size: "40.5 MB" }
  ]);

  // Ad Preview Panel State - Configured as a fully functional and editable preview model
  const [selectedPreviewAd, setSelectedPreviewAd] = useState<any>({
    id: "APP-001",
    title: "Eleanor Vance UGC Ad Segment (15s)",
    platform: "LinkedIn",
    previewType: "feed", // "feed" | "mobile" | "story"
    avatar: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=256&auto=format&fit=crop", // Elegant professional photo for Eleanor
    body: "Our Principal Private Secretaries are fully synchronized to the NexoraX NexoraX Resource resource, routing corporate traffic with absolute speed... Zero initial payment. Fees completely absorbed under NexoraX billing records scheme.",
    mediaUrl: "https://images.unsplash.com/photo-1557804506-669a67965ba0?q=80&w=3274&auto=format&fit=crop",
    likes: 384,
    comments: 42,
    clicks: 1240,
    ctr: "94% target calibrated (5.42%)"
  });

  // Auto Scale Toggle State
  const [autoScale, setAutoScale] = useState<boolean>(true);

  // Request Form validation state list
  const [validationAlerts, setValidationAlerts] = useState<string[]>([]);

  // Filtered and generic getters for client-facing view
  const getFilteredCampaignHistory = () => {
    if (role === "Client") {
      return campaignHistory.map(item => {
        if (item.id === "REQ-109") {
          return {
            ...item,
            goal: "Summer Collection Video Promo",
            audience: "Fashion Enthusiasts",
            product: "Organic Cotton Apparel Reel",
            offer: "20% off Launch Promo with Custom Visuals",
            notes: "Premium lifestyle theme with smooth transitions in high fidelity."
          };
        }
        if (item.id === "REQ-108") {
          return {
            ...item,
            goal: "Core Brand Awareness Video",
            audience: "E-Commerce Partners",
            product: "Conversion Analytics Dashboard Promo",
            offer: "Secure Stripe Integration Demonstration",
            notes: "Showcase real-time conversion metrics."
          };
        }
        return item;
      });
    }
    return campaignHistory;
  };

  const getFilteredApprovalItems = () => {
    if (role === "Client") {
      return approvalItems.map(item => {
        if (item.id === "APP-001") {
          return {
            ...item,
            title: "Summer Collection Reel (15s)",
            tagline: "High impact CTR Calibration (92%)",
            comments: "Review transition timing. Ensure the promo code 'SUMMER20' text is clearly legible.",
            caption: "Elevate your wardrobe this season with our sustainable, organic cotton collection. Handcrafted for maximum comfort and style."
          };
        }
        if (item.id === "APP-002") {
          return {
            ...item,
            title: "NexoraX Analytics Dashboard Render",
            tagline: "Interactive Conversion Visual Panel",
            comments: "Perfect render resolution. The custom brand presets have been integrated successfully.",
            caption: "Track and scale your store performance securely. Real-time insights, zero conversion lag, and enterprise-level speed."
          };
        }
        if (item.id === "APP-003") {
          return {
            ...item,
            title: "Brand Launch Campaign Copy v1.2",
            tagline: "High-density Marketing Asset Copy",
            comments: "Excellent copy and call to action placement. Tested safe for social distributions.",
            caption: "Discover the next evolution of AI-driven creative automation. Seamless request queues, direct publishing channels, and elite brand growth..."
          };
        }
        return item;
      });
    }
    return approvalItems;
  };

  const getFilteredCalendarEvents = () => {
    if (role === "Client") {
      return calendarEvents.map(event => {
        if (event.title.includes("Secure Payment Provider")) {
          return { ...event, title: "Summer Collection Promo Launch" };
        }
        if (event.title.includes("Eleanor")) {
          return { ...event, title: "Store Conversion Analytics Reel" };
        }
        if (event.title.includes("SecurePayment")) {
          return { ...event, title: "Product Features Highlight Ad" };
        }
        if (event.title.includes("Institutional")) {
          return { ...event, title: "Sustainable Apparel Campaign" };
        }
        return event;
      });
    }
    return calendarEvents;
  };

  const getFilteredSupportMessages = () => {
    if (role === "Client") {
      return supportMessages.map(msg => {
        if (msg.sender === "Director Mahmood") {
          return {
            ...msg,
            sender: "Technical Director",
            text: "Please confirm that all summer launch assets have the correct high-resolution color profile.",
            role: "Manager"
          };
        }
        if (msg.sender === "Eleanor Vance (Client)") {
          return {
            ...msg,
            sender: "Brand Client (E-Com)",
            text: "Revision requested on UGC reel. Adjusting discount card placement to keep the focus on apparel details.",
            role: "Client"
          };
        }
        if (msg.sender === "Staff Coordinator (Tech Team)") {
          return {
            ...msg,
            sender: "Sophia (Creative Office)",
            text: "Checked. We calibrated the rendering canvas. Version 2.4 contains the corrected placement and brand style.",
            role: "Agency/Admin"
          };
        }
        return msg;
      });
    }
    return supportMessages;
  };

  const getFilteredSelectedPreviewAd = () => {
    if (role === "Client") {
      const filteredItem = getFilteredApprovalItems().find(item => item.id === selectedPreviewAd.id);
      if (filteredItem) {
        return {
          ...selectedPreviewAd,
          title: filteredItem.title,
          body: filteredItem.caption,
          ctr: "92% target calibrated (5.10%)"
        };
      }
    }
    return selectedPreviewAd;
  };
  
  // Trend indicator dashboard states
  const [activeTrendTag, setActiveTrendTag] = useState<string>("NexoraX billing records");
  const [trendStats, setTrendStats] = useState<any[]>([
    { tag: "NexoraX billing records", searches: "18.4K", change: "+143% surge", ctrTarget: "6.2%", difficulty: "Low" },
    { tag: "Private Secretary Acquisition", searches: "12.1K", change: "+84% surge", ctrTarget: "5.8%", difficulty: "Medium" },
    { tag: "Secure Payment Provider Grant Placement", searches: "9.5K", change: "+210% surge", ctrTarget: "7.1%", difficulty: "Low" },
    { tag: "AI NexoraX Integration", searches: "15.2K", change: "+92% rise", ctrTarget: "5.5%", difficulty: "High" },
    { tag: "SC887095 Registry Lookup", searches: "4.8K", change: "+350% skyrocket", ctrTarget: "8.4%", difficulty: "Low" }
  ]);

  // Sync initial metrics from localStorage if present
  useEffect(() => {
    const fetchEcosystemKPIs = () => {
      const generatedCount = localStorage.getItem("nx_total_campaigns_generated");
      if (generatedCount) {
        setVideosGenerated(parseInt(localStorage.getItem("nx_total_ugc_videos_rendered") || "48", 10));
        setCampaignsCreated(parseInt(generatedCount, 10));
        const ctval = parseFloat(localStorage.getItem("nx_ad_factory_avg_ctr") || "5.42");
        setPerformanceData(prev => ({
          ...prev,
          ctr: `${ctval}%`
        }));
      }
    };
    fetchEcosystemKPIs();
    // Add real-time event listener for localstorage changes
    window.addEventListener("storage", fetchEcosystemKPIs);
    return () => window.removeEventListener("storage", fetchEcosystemKPIs);
  }, []);

  // --- HANDLERS ---
  const handleRoleToggle = () => {
    let nextRole: "Client" | "Agency/Admin" | "Super Admin" = "Client";
    if (role === "Client") nextRole = "Agency/Admin";
    else if (role === "Agency/Admin") nextRole = "Super Admin";
    else nextRole = "Client";
    setRole(nextRole);
    if (nextRole === "Client" && (activeTab === "agency" || activeTab === "superadmin")) {
      setActiveTab("dashboard");
    }
    triggerToast(`Role permissions swapped to: ${nextRole}`, "info");
  };

  // Bulk Export Simulation State Machine
  const handleBulkExport = () => {
    if (exportStatus !== "idle") return;
    
    setExportStatus("preparing");
    setExportProgress(10);
    setExportTrace("[Node-17 Chronos Ledger] Handshaking database anchors... Connecting CloudSync-02 Vault encryption buffers.");
    triggerToast("Initializing secure NexoraX content export...", "info");

    setTimeout(() => {
      setExportStatus("packaging");
      setExportProgress(45);
      setExportTrace("[CloudSync-02 Vault] Packaging 5 assets. Compressing horizontal MP4 elements, high-definition PNG cards, and JSON voice maps.");
    }, 1200);

    setTimeout(() => {
      setExportStatus("finalizing");
      setExportProgress(80);
      setExportTrace("[Secure Payment Provider Sync] Injecting 1% 3% Community Allocation codes and verifying NI: NH******B authorization certificates.");
    }, 2800);

    setTimeout(() => {
      setExportStatus("completed");
      setExportProgress(100);
      setExportTrace("[NexoraX Secure Gate] Campaign bundle exported! 5 files archived in nexorax_campaign_export.zip.");
      
      const newExport = {
        id: `EXP-${Math.floor(Math.random() * 9000) + 1000}`,
        name: `nexorax_campaign_export_${Math.floor(Math.random() * 900) + 100}.zip`,
        count: assets.length,
        date: new Date().toISOString().split("T")[0],
        status: "Ready",
        size: `${(assets.length * 10.4).toFixed(1)} MB`
      };
      setExportHistory(prev => [newExport, ...prev]);
      triggerToast("NexoraX Export Package Compiled Successfully!", "success");
    }, 4500);
  };

  const resetExportStatus = () => {
    setExportStatus("idle");
    setExportProgress(0);
    setExportTrace("");
  };

  // Campaign Request validation & Submission Handlers
  const handleCampaignSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset validation errors
    const errors: string[] = [];
    
    if (!campaignGoal) {
      errors.push("Campaign Purpose / Goal is required.");
    }
    if (!campaignTargetAudience || campaignTargetAudience.trim().length < 5) {
      errors.push("Target Audience description is required and must be at least 5 characters.");
    }
    if (!campaignProduct || campaignProduct.trim().length < 5) {
      errors.push("Product details are required to guarantee exact custom calibration.");
    }
    if (!campaignDeadline) {
      errors.push("Launch Deadline date must be specified.");
    } else {
      const deadlineDate = new Date(campaignDeadline);
      const currentDate = new Date();
      currentDate.setHours(0,0,0,0);
      if (deadlineDate < currentDate) {
        errors.push("Launch Deadline cannot be in past chronological timeline.");
      }
    }
    
    // Budget check: ensure numeric and meets NexoraX threshold limits
    const rawBudgetStr = campaignBudget.replace(/[^0-9.]/g, "");
    const budgetVal = parseFloat(rawBudgetStr);
    if (!campaignBudget) {
      errors.push("Target Budget is required.");
    } else if (isNaN(budgetVal) || budgetVal < 500) {
      errors.push("Minimum budget of £500 is required to calibrate Node-06 Oracle sensors.");
    }

    if (!campaignNotes || campaignNotes.trim().length < 15) {
      errors.push("Detailed Notes are required (minimum 15 chars) to prevent style dilution and digital drifts.");
    }

    if (errors.length > 0) {
      setValidationAlerts(errors);
      triggerToast("NexoraX validation threshold failed! Review notes.", "error");
      return;
    }

    // Success validation
    setValidationAlerts([]);

    const newRequest = {
      id: `REQ-${Math.floor(Math.random() * 900) + 200}`,
      goal: campaignGoal,
      audience: campaignTargetAudience,
      product: campaignProduct,
      offer: campaignOffer,
      platform: campaignPlatform,
      budget: campaignBudget,
      deadline: campaignDeadline,
      notes: campaignNotes,
      status: "Analyzing Strategy",
      dateSubmitted: new Date().toISOString().split("T")[0]
    };

    setCampaignHistory([newRequest, ...campaignHistory]);
    
    // Save live KPI increments locally
    const totalCampaigns = parseInt(localStorage.getItem("nx_total_campaigns_generated") || "14", 10) + 1;
    localStorage.setItem("nx_total_campaigns_generated", totalCampaigns.toString());

    setActiveCampaigns(prev => prev + 1);
    setVideosGenerated(prev => prev + 1);

    triggerToast("Campaign Request Dispatched to NexoraX AI Command Center!", "success");
    setActiveTab("timeline"); // Redirect to timeline
  };

  const handleUpdateBrandProfile = (e: React.FormEvent) => {
    e.preventDefault();
    localStorage.setItem("nx_best_lang", "English Baseline & Arabic Localised Style");
    localStorage.setItem("nx_best_audience", brandAudience);
    localStorage.setItem("nx_best_cta", `“${brandCtaRules}”`);
    
    // Trigger globally tracked event in other tabs if open
    window.dispatchEvent(new Event("storage"));

    triggerToast("Brand Voice Profile Synced with NexoraX Resource Library!", "success");
  };

  const handleApproveItem = (id: string) => {
    setApprovalItems(prev =>
      prev.map(item => (item.id === id ? { ...item, status: "Approved" } : item))
    );
    setPendingApprovalsCount(prev => Math.max(0, prev - 1));
    triggerToast(`Asset ${id} fully signed off. Replicating state...`, "success");
  };

  const handleRequestChanges = (id: string, notes: string) => {
    if (!notes) {
      triggerToast("Please enter notes or comments detailing changes requested", "warning");
      return;
    }
    setApprovalItems(prev =>
      prev.map(item => (item.id === id ? { ...item, status: "Revision Requested", comments: notes } : item))
    );
    triggerToast(`Revision dispatch logged under sequence ${id}`, "info");
  };

  const handlePostComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const msg = {
      sender: role === "Client" ? "Eleanor Vance (Client)" : "NexoraX Admin Team",
      text: newMessage,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      role: role
    };

    setSupportMessages([...supportMessages, msg]);
    setNewMessage("");
    triggerToast("Comment updated in campaign trace history.", "success");

    // Simulate fast architect replies automatically
    setTimeout(() => {
      const response = {
        sender: "NexoraX Neural OS",
        text: `Handshake confirmed. Syncing comments to CloudSync-02 Vault. Resolution benchmarked at 0.8s. Current active state: ONLINE.`,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        role: "Agency/Admin"
      };
      setSupportMessages(prev => [...prev, response]);
    }, 900);
  };

  return (
    <div className="space-y-6">
      {/* 1. HEADER SECTION */}
      <div className="relative p-6 rounded-3xl bg-gradient-to-r from-purple-950/60 to-cyan-950/50 border border-purple-500/25 overflow-hidden shadow-[inset_0_2px_20px_rgba(168,85,247,0.1)] text-left">
        <div className="absolute top-2 right-2 p-3 flex items-center gap-3">
          <span className="text-[8px] font-mono bg-cyan-950/80 text-cyan-400 px-2.5 py-1 rounded-full border border-cyan-500/30 font-black tracking-widest uppercase hidden sm:inline-block">
            SaaS PORTAL v9.2
          </span>
          
          {/* ELEANOR ACCOUNT AVATAR DROPDOWN */}
          <div className="relative group/profile">
            <button
              id="btn-profile-dropdown"
              className="flex items-center gap-2 p-1.5 bg-black/60 hover:bg-black/90 border border-purple-500/30 rounded-xl cursor-pointer text-left transition duration-200"
            >
              <img
                src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=256&auto=format&fit=crop"
                alt="Account Avatar"
                className="w-7 h-7 rounded-lg object-cover border border-purple-400/40"
                referrerPolicy="no-referrer"
              />
              <div className="hidden md:block pr-1">
                <p className="text-[10px] text-white font-black uppercase leading-tight font-sans">Eleanor Vance</p>
                <p className="text-[7.5px] text-purple-300 font-mono tracking-wider leading-none uppercase">{role}</p>
              </div>
            </button>
            
            {/* Hover Group Dropdown */}
            <div className="absolute right-0 mt-1.5 w-52 bg-[#090312] border border-purple-500/35 rounded-2xl p-2.5 shadow-[0_10px_30px_rgba(0,0,0,0.9)] opacity-0 invisible group-hover/profile:opacity-100 group-hover/profile:visible transition-all duration-200 z-50 text-xs">
              <div className="border-b border-white/5 pb-2 mb-2">
                <p className="text-[#00e5ff] font-sans font-black text-[10px] uppercase">Secure Workspace</p>
                <p className="text-[8.5px] text-zinc-500 font-mono">ID: SEC-8b1d48cf-525c</p>
              </div>
              
              <div className="space-y-1 text-left">
                <button
                  type="button"
                  onClick={() => triggerToast("Directing to Account Preferences Panel", "info")}
                  className="w-full text-left p-1.5 hover:bg-white/5 rounded-lg text-zinc-300 hover:text-white transition text-[9.5px] uppercase font-bold"
                >
                  Account Profile
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab("billing");
                    triggerToast("Loaded secure Stripe subscription manager", "info");
                  }
                  }
                  className="w-full text-left p-1.5 hover:bg-white/5 rounded-lg text-zinc-300 hover:text-white transition text-[9.5px] uppercase font-bold"
                >
                  Stripe Billing
                </button>
              </div>

              {isAllowedToSwitchRoles && (
                <div className="border-t border-white/5 pt-2 mt-2 space-y-1.5 pl-0 text-left">
                  <p className="text-[7.5px] font-mono text-zinc-500 uppercase tracking-widest block text-left">SaaS Demo Controls</p>
                  <div className="grid grid-cols-3 gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        setRole("Client");
                        if (activeTab === "agency" || activeTab === "superadmin") {
                          setActiveTab("dashboard");
                        }
                        triggerToast("Switched to Client Persona", "info");
                      }}
                      className={`p-1 rounded text-[7.5px] font-mono text-center uppercase font-black cursor-pointer border ${
                        role === "Client"
                          ? "bg-purple-950/80 text-purple-300 border-purple-500/40"
                          : "bg-black/40 text-rose-300 border-transparent hover:text-zinc-650"
                      }`}
                    >
                      Client
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setRole("Agency/Admin");
                        triggerToast("Switched to Agency/Admin Persona", "success");
                      }}
                      className={`p-1 rounded text-[7.5px] font-mono text-center uppercase font-black cursor-pointer border ${
                        role === "Agency/Admin"
                          ? "bg-cyan-950/80 text-cyan-300 border-cyan-500/40"
                          : "bg-black/40 text-zinc-500 border-transparent hover:text-zinc-300"
                      }`}
                    >
                      Admin
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setRole("Super Admin");
                        triggerToast("Switched to Super Admin Persona", "success");
                      }}
                      className={`p-1 rounded text-[7.5px] font-mono text-center uppercase font-black cursor-pointer border ${
                        role === "Super Admin"
                          ? "bg-pink-950/80 text-pink-300 border-pink-500/40"
                          : "bg-black/40 text-zinc-500 border-transparent hover:text-zinc-300"
                      }`}
                    >
                      S.Admin
                    </button>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>

        <div className="max-w-2xl space-y-2">
          <span className="text-[8.5px] font-mono text-[#00e5ff] uppercase tracking-widest font-black block">
            NEXORAX COMMERCIAL EXECUTIVE CLIENT PORTAL
          </span>
          <h2 className="text-xl md:text-3xl font-sans font-black text-white tracking-tight leading-none uppercase">
            Client Campaign Workspace
          </h2>
          <p className="text-xs font-mono font-bold text-purple-300">
            Request. Review. Approve. Publish. Grow.
          </p>
          <p className="text-zinc-400 text-[11px] md:text-xs leading-relaxed pt-1">
            The NexoraX corporate matrix dashboard connecting external enterprise partners and digital-starved clients directly with our master neural generation pipeline, accounting registries, and direct billing setups.
          </p>
        </div>
      </div>

      {/* 2. TOP KPI PANEL: CLIENT GROWTH OVERVIEW */}
      <div className="bg-black/40 rounded-3xl border border-purple-500/15 p-5 text-left">
        <div className="flex items-center justify-between mb-4 border-b border-white/5 pb-2">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
            <h4 className="text-[11px] font-mono font-bold text-white uppercase tracking-widest">
              ★ Client Growth Overview
            </h4>
          </div>
          <span className="text-[8px] font-mono text-zinc-500 uppercase">
            Active Real-time calibration
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
          {[
            { label: "Active Campaigns", val: activeCampaigns, sub: "+2 active this week", color: "text-cyan-400" },
            { label: "Pending Approvals", val: pendingApprovalsCount, sub: `${pendingApprovalsCount} assets ready`, color: "text-amber-400 animate-pulse" },
            { label: "Videos Generated", val: videosGenerated, sub: "High-CTR calibrated", color: "text-purple-400" },
            { label: "Posts Scheduled", val: postsScheduled, sub: "Social networks queued", color: "text-blue-400" },
            { label: "Leads Generated", val: leadsGenerated, sub: "100% genuine CTR", color: "text-[#00e5ff]" },
            { label: "Monthly Spend", val: `£${(monthlySpend).toLocaleString()}`, sub: role === "Client" ? "Stripe Automated Sweep" : "SecurePayment Route Sweep", color: "text-[#e840f3]" },
            { label: "ROI Forecast", val: roiForecast, sub: "NexoraX Target Achieved", color: "text-emerald-400 font-bold" }
          ].map((kpi, idx) => (
            <div key={idx} className="bg-[#0c051a]/70 p-3 rounded-2xl border border-white/5 flex flex-col justify-between">
              <span className="text-[8px] font-mono text-zinc-500 uppercase font-black leading-tight block mb-1">
                {kpi.label}
              </span>
              <div className="my-1.5 flex items-baseline gap-1">
                <span className={`text-xl md:text-2xl font-mono font-black ${kpi.color}`}>
                  {kpi.val}
                </span>
              </div>
              <span className="text-[7.5px] font-mono text-zinc-400 block truncate">
                {kpi.sub}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* 3. CORE SUB-MENU NAVIGATION BAR */}
      <div className="flex flex-wrap gap-1.5 p-1 bg-black/60 rounded-2xl border border-white/5 overflow-x-auto">
        {[
          { id: "dashboard", label: "Dashboard", count: null, roles: ["Client", "Agency/Admin", "Super Admin"] },
          { id: "request", label: "Request Builder", count: getFilteredCampaignHistory().length, roles: ["Client", "Agency/Admin", "Super Admin"] },
          { id: "brand", label: "Brand Profile", count: null, roles: ["Client", "Agency/Admin", "Super Admin"] },
          { id: "approval", label: "Approval Desk", count: getFilteredApprovalItems().filter(x => x.status === "Pending Review").length, roles: ["Client", "Agency/Admin", "Super Admin"] },
          { id: "timeline", label: "Workflow Timeline", count: null, roles: ["Client", "Agency/Admin", "Super Admin"] },
          { id: "assets", label: "Asset Library", count: assets.filter(a => role !== "Client" || a.id !== "AST-8203").length, roles: ["Client", "Agency/Admin", "Super Admin"] },
          { id: "calendar", label: "Publish Calendar", count: getFilteredCalendarEvents().filter(e => e.approvalState === "Ready").length, roles: ["Client", "Agency/Admin", "Super Admin"] },
          { id: "performance", label: "Performance", count: null, roles: ["Client", "Agency/Admin", "Super Admin"] },
          { id: "billing", label: "Billing", count: null, roles: ["Client", "Agency/Admin", "Super Admin"] },
          { id: "support", label: "Support & Collab", count: getFilteredSupportMessages().length, roles: ["Client", "Agency/Admin", "Super Admin"] },
          { id: "agency", label: "★ Agency Console", count: 3, roles: ["Agency/Admin", "Super Admin"] },
          { id: "superadmin", label: "⚡ Super Admin Control", count: null, roles: ["Super Admin"] }
        ]
        .filter(tab => tab.roles.includes(role))
        .map(tab => (
          <button
            key={tab.id}
            id={`tab-layout-${tab.id}`}
            onClick={() => setActiveTab(tab.id as any)}
            className={`px-3 py-2 rounded-xl text-[9px] sm:text-[10px] uppercase font-black tracking-wider transition-all duration-200 cursor-pointer flex items-center gap-1.5 ${
              activeTab === tab.id
                ? "bg-gradient-to-r from-purple-900/50 to-cyan-900/50 text-white border border-purple-500/30 font-black shadow-[0_0_10px_rgba(168,85,247,0.1)]"
                : "text-zinc-400 hover:text-white hover:bg-white/5 border border-transparent"
            }`}
          >
            <span>{tab.label}</span>
            {tab.count !== null && (
              <span className="px-1.5 py-0.5 rounded-full bg-black text-cyan-400 border border-cyan-500/20 text-[7px] font-mono">
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* 4. DYNAMIC SUB-TABS WORKSPACE CONTENT */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
          className="text-left"
        >
          {/* === 1. CLIENT DASHBOARD === */}
          {activeTab === "dashboard" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Subscription card */}
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-black/70 rounded-3xl border border-purple-500/20 p-6 space-y-4">
                  <div className="flex justify-between items-start">
                    <div className="space-y-1 text-left">
                      <span className="text-[9px] font-mono text-[#00e5ff] uppercase tracking-widest font-black block">Active Plan</span>
                      <h3 className="text-xl font-sans font-black text-white uppercase tracking-tight">
                        {subscriptionPlan}
                      </h3>
                      <p className="text-xs text-purple-300">
                        {role === "Client" 
                          ? "Encrypted corporate access license fully secured via Stripe integration." 
                          : "Directly backed by NexoraX billing records scheme (NI: NH******B)"}
                      </p>
                    </div>
                    <span className="bg-purple-950/80 text-purple-300 px-3 py-1 rounded-xl text-[9px] font-mono border border-purple-500/30 uppercase font-black tracking-wider animate-pulse">
                      Active Enterprise
                    </span>
                  </div>

                  <div className="border-t border-white/5 pt-4 grid grid-cols-2 gap-4 text-xs font-sans">
                    <div className="text-left">
                      <span className="text-zinc-500 block text-[9.5px] uppercase font-bold">Client Business Entity</span>
                      <span className="text-white font-medium block mt-1">NexoraX Digital Ltd (SC887095)</span>
                    </div>
                    <div className="text-left">
                      <span className="text-zinc-500 block text-[9.5px] uppercase font-bold">Service Node Authority</span>
                      <span className="text-cyan-400 font-mono text-[10.5px] block mt-1">
                        {role === "Client" ? "ACTIVE-PORTAL-UK-WEST" : "NEST-MIGRATE-Staff Coordinator"}
                      </span>
                    </div>
                  </div>

                  <div className="border-t border-white/5 pt-4 space-y-3">
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-zinc-400">Total Campaign Render Buffer Usage</span>
                      <span className="font-mono text-white font-black">840 / 1000 AI Credits consumed</span>
                    </div>
                    <div className="w-full bg-white/5 h-2 rounded-full overflow-hidden border border-white/5">
                      <div className="h-full bg-gradient-to-r from-purple-500 to-cyan-400" style={{ width: "84%" }}></div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center text-[10.5px] font-mono pt-2">
                    <div className="flex items-center gap-1.5 text-zinc-500">
                      <Clock size={11} className="text-purple-400" />
                      <span>Next Invoice billing calibration: June 20, 2026</span>
                    </div>
                    <span className="text-amber-400 font-bold">84% CAPACITY USE</span>
                  </div>
                </div>

                {/* Status Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                  <div className="bg-[#070411]/85 rounded-3xl border border-white/5 p-5 space-y-2">
                    <h4 className="text-xs font-mono font-bold text-[#00e5ff] uppercase tracking-wider flex items-center gap-1.5">
                      <Zap size={11} />
                      Active Subscribed Services
                    </h4>
                    <div className="space-y-1.5 text-[11px] text-zinc-400">
                      <div className="flex justify-between border-b border-white/5 pb-10">
                        <span>AI Video Campaign Engine</span>
                        <span className="text-white font-black">ACTIVE</span>
                      </div>
                      <div className="flex justify-between border-b border-white/5 pb-1">
                        <span>UGC Creator Suite (Voiceover Profile)</span>
                        <span className="text-white font-black">ACTIVE</span>
                      </div>
                      <div className="flex justify-between border-b border-white/5 pb-1">
                        <span>
                          {role === "Client" 
                            ? "Sall Billing Sync Node" 
                            : "SecurePayment Auto Ledger Mirror Node"}
                        </span>
                        <span className="text-white font-black">ACTIVE</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Dedicated Imperial Google Drive Vault</span>
                        <span className="text-[#00e5ff] font-mono font-bold">SECURE</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#070411]/85 rounded-3xl border border-white/5 p-5 space-y-2">
                    <h4 className="text-xs font-mono font-bold text-purple-400 uppercase tracking-wider flex items-center gap-1.5">
                      <Users size={11} />
                      Licensed Portrait Avatars
                    </h4>
                    <div className="space-y-1.5 text-[11px] text-zinc-400">
                      <div className="flex justify-between border-b border-white/5 pb-1">
                        <span>Eleanor Vance (Licensed)</span>
                        <span className="text-purple-300 font-bold">94% CTR calibration</span>
                      </div>
                      {role === "Client" ? (
                        <div className="flex justify-between border-b border-white/5 pb-1">
                          <span>Charlotte Young (Licensed)</span>
                          <span className="text-cyan-300 font-bold">91% CTR Calibration</span>
                        </div>
                      ) : (
                        <div className="flex justify-between border-b border-white/5 pb-1">
                          <span>Staff Coordinator (Pakistan Remote Node)</span>
                          <span className="text-yellow-400 font-[#e840f3] font-black">Pending_Migration</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span>Active Campaign Streams</span>
                        <span className="text-white font-mono font-bold">2 Live Channels</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SECURE CLIENT TRUST INDICATORS & SIMPLIFIED AUDIT HISTORY (EXCLUSIVE TO CLIENT PERSONA) */}
                {role === "Client" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                    {/* Security indicators */}
                    <div className="bg-[#040915]/90 rounded-3xl border border-cyan-500/20 p-5 space-y-3 shadow-[0_0_15px_rgba(0,229,255,0.02)]">
                      <h4 className="text-xs font-mono font-black text-cyan-400 uppercase tracking-widest border-b border-white/5 pb-2">
                        🛡 BRAND SEGREGATION & TRUST
                      </h4>
                      <ul className="space-y-2 text-[10.5px] text-zinc-300">
                        <li className="flex items-center gap-2">
                          <CheckCircle2 size={12} className="text-cyan-400 shrink-0" />
                          <span><strong>Secure client workspace:</strong> Isolated multi-tenant database session.</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 size={12} className="text-cyan-400 shrink-0" />
                          <span><strong>Role-protected access:</strong> Single-signer authorization flow active.</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 size={12} className="text-cyan-400 shrink-0" />
                          <span><strong>Payment secured by Stripe:</strong> Encrypted SaaS invoice billing sweep.</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 size={12} className="text-cyan-400 shrink-0" />
                          <span><strong>Approval history recorded:</strong> Cryptographic sign-off immutable ledger.</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 size={12} className="text-cyan-400 shrink-0" />
                          <span><strong>Client data isolated:</strong> Sandbox brand assets never cached or shared.</span>
                        </li>
                      </ul>
                      <div className="pt-1.5 border-t border-white/5 text-[9.5px] font-mono text-cyan-300/80 italic">
                        "Only use licensed avatars, approved brand assets, or client-uploaded media with valid permission."
                      </div>
                    </div>

                    {/* Simplified Client-Visible Audit history */}
                    <div className="bg-[#090412]/85 rounded-3xl border border-purple-500/20 p-5 space-y-3">
                      <h4 className="text-xs font-mono font-black text-[#e840f3] uppercase tracking-widest border-b border-white/5 pb-2">
                        ⚡ CAMPAIGN ACTION TRAIL
                      </h4>
                      <div className="space-y-2.5 max-h-[170px] overflow-y-auto pr-1">
                        {[
                          { action: "Campaign requested", ref: "LinkedIn Video Ad", date: "June 12, 2026 - 11:23 AM", status: "QUEUED" },
                          { action: "Asset generated", ref: "Eleanor Vance Version 2", date: "June 11, 2026 - 04:42 PM", status: "SUCCESS" },
                          { action: "Revision requested", ref: "Ad Copy Adjustments", date: "June 11, 2026 - 02:15 PM", status: "STUCTURED" },
                          { action: "Asset approved", ref: "LinkedIn Promo Banner", date: "June 10, 2026 - 09:30 AM", status: "COMPLETED" },
                          { action: "Post scheduled", ref: "Weekly Client Resource Workflow Ad", date: "June 10, 2026 - 09:35 AM", status: "SCHEDULED" },
                          { action: "Post published", ref: "NexoraX Resource Launch Announcement", date: "June 09, 2026 - 12:00 PM", status: "PUBLISHED" }
                        ].map((audit, i) => (
                          <div key={i} className="flex justify-between items-start text-[10px] font-mono border-b border-white/5 pb-1.5 last:border-0 last:pb-0">
                            <div>
                              <p className="text-white font-bold">{audit.action}</p>
                              <p className="text-zinc-500 text-[8.5px]">{audit.ref}</p>
                            </div>
                            <div className="text-right">
                              <span className="text-[#00e5ff] text-[8.5px] block font-black">{audit.status}</span>
                              <span className="text-zinc-500 text-[8px] block">{audit.date}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Side Status Widget */}
              <div className="space-y-6">
                <div className="bg-gradient-to-b from-[#180825] to-[#04010a]/90 rounded-3xl border border-purple-500/20 p-5 space-y-4">
                  <h4 className="text-xs font-mono font-bold text-[#e840f3] uppercase tracking-widest border-b border-white/5 pb-2">
                    ★ {role === "Client" ? "COMMERCIAL DIRECTIVES" : "EXECUTIVE DIRECTIVES"}
                  </h4>
                  <div className="p-3.5 bg-black/60 rounded-xl border border-purple-500/10 text-xs space-y-2">
                    {role === "Client" ? (
                      <p className="text-zinc-300 leading-relaxed font-sans text-[11px]">
                        "Welcome to the NexoraX Client Campaign Workspace. Seamlessly coordinate with our creative studio, approve high-conversion assets, and schedule instant cross-platform deployments with 100% brand safety."
                      </p>
                    ) : (
                      <p className="text-zinc-300 leading-relaxed font-sans text-[11px]">
                        "This acquisition is structured under a strict 'Zero-Fund' Services Guide. All remuneration and agency placement fees must be anchored 100% via the NexoraX billing records scheme..."
                      </p>
                    )}
                    <span className="block font-mono font-bold text-white text-[9.5px]">
                      — Office of the Director
                    </span>
                  </div>

                  <div className="border-t border-white/5 pt-4 text-[10.5px] font-mono text-zinc-400 space-y-2.5 text-left">
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                      <span>Freephone anchor: 08000 622827</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                      <span>Inbound Mail: support@nexoraxdigital.co.uk</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                      <span>Ecosystem Standby Latency: 0.8s OK</span>
                    </div>
                  </div>

                  <button
                    id="upgrade-plan-dashboard-shortcut"
                    onClick={() => setActiveTab("billing")}
                    className="w-full py-2.5 bg-gradient-to-r from-purple-600 to-cyan-500 hover:opacity-95 text-black font-sans font-black text-[9.5px] uppercase tracking-widest rounded-xl transition cursor-pointer"
                  >
                    Manage Billing / Upgrade
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* === 2. CAMPAIGN REQUEST BUILDER === */}
          {activeTab === "request" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-black/70 rounded-3xl border border-purple-500/20 p-6 space-y-4">
                <div className="flex items-center justify-between border-b border-white/5 pb-3">
                  <h3 className="text-xl font-sans font-black text-white uppercase tracking-tight">
                    Campaign Request Builder
                  </h3>
                  <span className="text-[8.5px] font-mono bg-purple-950 text-purple-300 px-2 py-0.5 rounded border border-purple-500/30 uppercase font-bold">
                    SYSTEM SECURE
                  </span>
                </div>

                {/* Validation Warnings Panel */}
                {validationAlerts.length > 0 && (
                  <div className="p-4 bg-rose-950/40 border border-rose-500/30 rounded-2xl space-y-2 text-left text-xs font-mono">
                    <div className="flex items-center gap-2 text-rose-300 font-bold uppercase tracking-wider text-[10px]">
                      <AlertCircle size={15} className="text-rose-400" />
                      <span>Sponsorship Request Blocked — Validation Traces:</span>
                    </div>
                    <ul className="list-disc list-inside space-y-1 text-rose-200 pl-1 text-[11px] font-sans">
                      {validationAlerts.map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                    </ul>
                    <span className="block text-[8.5px] text-rose-400 font-black uppercase pt-1">
                      ERROR_MANDATE_VIOLATION: Calibration nodes rejected due to insufficient baseline payload.
                    </span>
                  </div>
                )}

                <form onSubmit={handleCampaignSubmit} className="space-y-4 text-left text-xs font-mono">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-zinc-400 uppercase font-black text-[9px] mb-1.5 block">Campaign Purpose / Goal</label>
                      <select
                        id="campaign-goal"
                        value={campaignGoal}
                        onChange={(e) => setCampaignGoal(e.target.value)}
                        className="w-full bg-zinc-950/80 border border-white/10 rounded-xl p-2.5 text-white focus:border-purple-500 focus:outline-none transition font-sans text-xs"
                      >
                        <option value="Lead Generation">Lead Generation (94% CTR Targets)</option>
                        <option value="Video Ad Campaign">Video Ad Campaign (NexoraX Core)</option>
                        <option value="UGC Video Campaign">UGC Video Campaign (Eleanor models)</option>
                        <option value="Product Promo">Product Promo Launch</option>
                        <option value="Brand Awareness">Brand Awareness</option>
                        <option value="Newsletter & Blog Article">Newsletter & Blog Series</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-zinc-400 uppercase font-black text-[9px] mb-1.5 block">Target Audience</label>
                      <input
                        id="campaign-target-audience"
                        type="text"
                        value={campaignTargetAudience}
                        onChange={(e) => setCampaignTargetAudience(e.target.value)}
                        className="w-full bg-zinc-950/80 border border-white/10 rounded-xl p-2.5 text-white focus:border-purple-500 focus:outline-none transition text-xs font-sans"
                        placeholder="Venture Recruiters & Private Secretaries"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-zinc-400 uppercase font-black text-[9px] mb-1.5 block">Product or Service Details</label>
                      <input
                        id="campaign-product"
                        type="text"
                        value={campaignProduct}
                        onChange={(e) => setCampaignProduct(e.target.value)}
                        className="w-full bg-zinc-950/80 border border-white/10 rounded-xl p-2.5 text-white focus:border-purple-500 focus:outline-none transition text-xs font-sans"
                        placeholder="NexoraX High-velocity AI Automations"
                      />
                    </div>

                    <div>
                      <label className="text-zinc-400 uppercase font-black text-[9px] mb-1.5 block">Direct Offer & Cost Guarantee</label>
                      <input
                        id="campaign-offer"
                        type="text"
                        value={campaignOffer}
                        onChange={(e) => setCampaignOffer(e.target.value)}
                        className="w-full bg-zinc-950/80 border border-white/10 rounded-xl p-2.5 text-white focus:border-purple-500 focus:outline-none transition text-xs font-sans"
                        placeholder={role === "Client" ? "Enter discount coupons or campaign offer parameters" : "NexoraX billing records direct billing under SC887095."}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-zinc-400 uppercase font-black text-[9px] mb-1.5 block">Preferred Live Platforms</label>
                      <select
                        id="campaign-platform"
                        value={campaignPlatform}
                        onChange={(e) => setCampaignPlatform(e.target.value)}
                        className="w-full bg-zinc-950/80 border border-white/10 rounded-xl p-2.5 text-white focus:border-purple-500 focus:outline-none transition font-sans text-xs"
                      >
                        <option value="LinkedIn">LinkedIn Sponsored Ads</option>
                        <option value="YouTube Long / Short">YouTube Cinematic</option>
                        <option value="TikTok Video Stream">TikTok Stream</option>
                        <option value="Meta (Insta/FB)">Instagram / Facebook Ads</option>
                        <option value="Cross-Channel All">All Cross-Channels Combined</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-zinc-400 uppercase font-black text-[9px] mb-1.5 block">Target Budget Allocation</label>
                      <input
                        id="campaign-budget"
                        type="text"
                        value={campaignBudget}
                        onChange={(e) => setCampaignBudget(e.target.value)}
                        className="w-full bg-zinc-950/80 border border-white/10 rounded-xl p-2.5 text-white focus:border-purple-500 focus:outline-none transition text-xs font-sans"
                        placeholder="£5,100"
                      />
                    </div>

                    <div>
                      <label className="text-zinc-400 uppercase font-black text-[9px] mb-1.5 block">Launch Deadline</label>
                      <input
                        id="campaign-deadline"
                        type="date"
                        value={campaignDeadline}
                        onChange={(e) => setCampaignDeadline(e.target.value)}
                        className="w-full bg-zinc-950/80 border border-white/10 rounded-xl p-2.5 text-white focus:border-purple-500 focus:outline-none transition text-xs font-sans"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-zinc-400 uppercase font-black text-[9px] mb-1.5 block">Ancillary Campaign Notes & Styling Directives</label>
                    <textarea
                      id="campaign-notes"
                      rows={4}
                      value={campaignNotes}
                      onChange={(e) => setCampaignNotes(e.target.value)}
                      className="w-full bg-zinc-950/80 border border-white/10 rounded-2xl p-3 text-white focus:border-purple-500 focus:outline-none transition text-xs font-sans leading-relaxed"
                      placeholder="Specify voice-over accents, visual color seeds, specific constraints etc..."
                    />
                  </div>

                  <div className="pt-2">
                    <button
                      id="btn-submit-campaign-request"
                      type="submit"
                      className="px-6 py-3 bg-[#e840f3] hover:opacity-90 text-black font-sans font-black text-[10px] uppercase tracking-widest rounded-xl transition hover:scale-[1.01] cursor-pointer"
                    >
                      Submit Campaign Request
                    </button>
                  </div>
                </form>
              </div>

              {/* History Track */}
              <div className="bg-[#070411]/85 rounded-3xl border border-white/5 p-5 space-y-4 text-left">
                <h4 className="text-xs font-mono font-bold text-purple-400 uppercase tracking-wider border-b border-white/5 pb-2 flex items-center gap-1.5">
                  <Workflow size={12} />
                  Active Client Request Queue
                </h4>

                <div className="space-y-3.5 max-h-[380px] overflow-y-auto pr-1">
                  {getFilteredCampaignHistory().map((item, idx) => (
                    <div key={idx} className="p-3 bg-zinc-950/60 rounded-xl border border-white/5 space-y-1.5">
                      <div className="flex justify-between items-center text-[9px] font-mono">
                        <span className="text-[#00e5ff] font-bold">{item.id}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${
                          item.status.includes("Active") ? "bg-cyan-900/40 text-cyan-300" : "bg-emerald-900/40 text-emerald-300"
                        }`}>{item.status}</span>
                      </div>
                      <h5 className="text-[11px] font-sans font-black text-white">{item.goal}</h5>
                      <p className="text-[10px] text-zinc-400 truncate"><strong className="text-zinc-500">Audience:</strong> {item.audience}</p>
                      <p className="text-[10px] text-zinc-400 truncate"><strong className="text-zinc-500">Platform:</strong> {item.platform}</p>
                      <div className="flex justify-between items-center text-[8.5px] font-mono text-zinc-500 pt-1 border-t border-white/5">
                        <span>Submitted: {item.dateSubmitted}</span>
                        <span className="text-zinc-400 font-bold">{item.budget}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* === 3. BRAND PROFILE === */}
          {activeTab === "brand" && (
            <div className="bg-black/70 rounded-3xl border border-purple-500/20 p-6 space-y-6">
              <div className="flex items-center justify-between border-b border-white/5 pb-3">
                <div className="space-y-1">
                  <h3 className="text-xl font-sans font-black text-white uppercase tracking-tight">
                    Brand Universe Configuration Profile
                  </h3>
                  <p className="text-zinc-400 text-xs">Calibrate the foundational vectors to guarantee zero digital drift or style dilution.</p>
                </div>
                <span className="text-[8.5px] font-mono bg-cyan-950 text-cyan-300 px-2 py-0.5 rounded border border-cyan-500/30 uppercase font-bold animate-pulse">
                  Vault Locked
                </span>
              </div>

              <form onSubmit={handleUpdateBrandProfile} className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs font-mono">
                <div className="space-y-4">
                  <div>
                    <label className="text-zinc-400 uppercase font-black text-[9px] mb-1.5 block">Brand Corporate Name</label>
                    <input
                      id="brand-name-input"
                      type="text"
                      className="w-full bg-zinc-950/80 border border-white/10 rounded-xl p-2.5 text-white text-xs font-sans"
                      value={brandName}
                      onChange={(e) => setBrandName(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="text-zinc-400 uppercase font-black text-[9px] mb-1.5 block">Brand Identity Primary & Accent Colours</label>
                    <input
                      id="brand-colors-input"
                      type="text"
                      className="w-full bg-zinc-950/80 border border-white/10 rounded-xl p-2.5 text-[#00e5ff] font-mono text-xs"
                      value={brandColors}
                      onChange={(e) => setBrandColors(e.target.value)}
                      placeholder="#a855f7, #00e5ff"
                    />
                  </div>

                  <div>
                    <label className="text-zinc-400 uppercase font-black text-[9px] mb-1.5 block">Corporate Tone of Voice</label>
                    <input
                      id="brand-tone-input"
                      type="text"
                      className="w-full bg-zinc-950/80 border border-white/10 rounded-xl p-2.5 text-white text-xs font-sans"
                      value={brandTone}
                      onChange={(e) => setBrandTone(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="text-zinc-400 uppercase font-black text-[9px] mb-1.5 block">Primary Target Website URL</label>
                    <input
                      id="brand-url-input"
                      type="text"
                      className="w-full bg-zinc-950/80 border border-white/10 rounded-xl p-2.5 text-white text-xs font-sans"
                      value={brandUrl}
                      onChange={(e) => setBrandUrl(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="text-zinc-400 uppercase font-black text-[9px] mb-1.5 block">Default Target Audience Bio</label>
                    <textarea
                      id="brand-audience-textarea"
                      rows={3}
                      className="w-full bg-zinc-950/80 border border-white/10 rounded-xl p-2.5 text-white text-xs font-sans leading-relaxed"
                      value={brandAudience}
                      onChange={(e) => setBrandAudience(e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-[#00e5ff] uppercase font-black text-[9px] mb-1.5 block">★ Mandatory Do Directives</label>
                    <textarea
                      id="brand-do-rules-textarea"
                      rows={2.5}
                      className="w-full bg-zinc-950/80 border border-cyan-500/25 rounded-xl p-2.5 text-white text-xs font-sans leading-relaxed"
                      value={brandDoRules}
                      onChange={(e) => setDoRules(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="text-rose-400 uppercase font-black text-[9px] mb-1.5 block">⚠ Forbidden Don’t Rules</label>
                    <textarea
                      id="brand-dont-rules-textarea"
                      rows={2.5}
                      className="w-full bg-zinc-950/80 border border-rose-500/25 rounded-xl p-2.5 text-white text-xs font-sans leading-relaxed"
                      value={brandDontRules}
                      onChange={(e) => setDontRules(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="text-[#e840f3] uppercase font-black text-[9px] mb-1.5 block">★ Compulsory CTA Framework Rules</label>
                    <input
                      id="brand-cta-rules-input"
                      type="text"
                      className="w-full bg-zinc-950/80 border border-purple-500/25 rounded-xl p-2.5 text-white text-xs font-sans"
                      value={brandCtaRules}
                      onChange={(e) => setCtaRules(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="text-zinc-400 uppercase font-black text-[9px] mb-1.5 block">High-Priority Copy Example Content</label>
                    <input
                      id="brand-example-input"
                      type="text"
                      className="w-full bg-zinc-950/80 border border-white/10 rounded-xl p-2.5 text-white text-xs font-sans"
                      value={brandExample}
                      onChange={(e) => setBrandExample(e.target.value)}
                    />
                  </div>

                  <div className="pt-2">
                    <button
                      id="btn-update-brand-profile"
                      type="submit"
                      className="w-full py-3 bg-cyan-400 hover:opacity-90 text-black font-sans font-black text-[10px] uppercase tracking-widest rounded-xl transition hover:scale-[1.01] cursor-pointer"
                    >
                      Update Brand Profile
                    </button>
                  </div>
                </div>
              </form>
            </div>
          )}

          {/* === 4. APPROVAL CENTER === */}
          {activeTab === "approval" && (
            <div className="space-y-6">
              {/* NexoraX Approvals Center Header */}
              <div className="bg-black/70 rounded-3xl border border-purple-500/25 p-6 space-y-4 relative overflow-hidden shadow-[0_0_20px_rgba(168,85,247,0.05)]">
                {/* Holographic Glowing grid background backplate */}
                <div className="absolute inset-0 bg-[radial-gradient(#a855f7_1px,transparent_1px)] [background-size:16px_16px] opacity-[0.03] pointer-events-none" />
                
                <div className="relative flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-white/5 pb-4">
                  <div className="text-left space-y-1">
                    <span className="text-[9px] font-mono text-cyan-400 font-bold uppercase tracking-widest block">
                      ★ NexoraX digital approval terminal (node-16)
                    </span>
                    <h3 className="text-2xl font-sans font-black text-white uppercase tracking-tight">
                      Holographic Approval Desk
                    </h3>
                    <p className="text-zinc-400 text-xs">Authorize synthesised campaigns under enterprise-grade, zero-drift verification signatures.</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="bg-[#e840f3]/10 text-[#e840f3] border border-[#e840f3]/25 px-2.5 py-1 rounded-xl text-[9px] font-mono font-black uppercase">
                      {pendingApprovalsCount} Pending Content Items
                    </span>
                    <button
                      id="btn-auto-scale-toggle-desk"
                      onClick={() => {
                        setAutoScale(!autoScale);
                        triggerToast(`Holographic viewport auto-scale: ${!autoScale ? "ENABLED (1.0x)" : "DISABLED (Fit)"}`, "info");
                      }}
                      className={`px-3 py-1 text-[9px] font-mono uppercase font-black rounded-lg border tracking-wider transition-all cursor-pointer flex items-center gap-1 ${
                        autoScale 
                          ? "bg-cyan-950/80 text-cyan-300 border-cyan-500/40 shadow-[0_0_8px_rgba(0,229,255,0.2)]" 
                          : "bg-zinc-950 text-zinc-500 border-white/10"
                      }`}
                    >
                      <Sliders size={11} className={autoScale ? "animate-pulse" : ""} />
                      <span>Auto Scale: {autoScale ? "ON" : "OFF"}</span>
                    </button>
                  </div>
                </div>

                {/* TWO COLUMN APPROVAL & DYNAMIC PREVIEW PANEL */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 relative">
                  
                  {/* Left Column (8 cols): Interactive Content Items and signoffs */}
                  <div className="lg:col-span-7 space-y-4 max-h-[700px] overflow-y-auto pr-1">
                    {getFilteredApprovalItems().map((item, idx) => {
                      const isSelected = selectedPreviewAd?.id === item.id;
                      return (
                        <div 
                          key={idx} 
                          onClick={() => {
                            setSelectedPreviewAd({
                              id: item.id,
                              title: item.title,
                              platform: item.platform.includes("LinkedIn") ? "LinkedIn" : item.platform.includes("YouTube") ? "YouTube" : "Instagram",
                              previewType: "feed",
                              avatar: item.id === "APP-001" ? "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=256" : "https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=256",
                              body: item.caption,
                              mediaUrl: item.playbackUrl,
                              likes: item.id === "APP-001" ? 384 : 142,
                              comments: item.id === "APP-001" ? 42 : 11,
                              clicks: item.id === "APP-001" ? 1240 : 490,
                              ctr: "94% CTR calibration sweep"
                            });
                            triggerToast(`Loaded ${item.id} preview calibration matrix`, "success");
                          }}
                          className={`p-5 rounded-2xl border transition-all duration-300 text-left relative overflow-hidden cursor-pointer ${
                            isSelected 
                              ? "bg-gradient-to-br from-purple-950/30 to-black/90 border-cyan-400 shadow-[0_0_15px_rgba(0,229,255,0.1)]" 
                              : "bg-zinc-950/70 border-white/10 hover:border-purple-500/20"
                          }`}
                        >
                          {/* Laser scanning line effect on selected element */}
                          {isSelected && (
                            <div className="absolute top-0 left-0 w-full h-[1.5px] bg-cyan-400 animate-[bounce_3s_infinite]" />
                          )}

                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-mono text-cyan-300 font-bold bg-cyan-950/50 px-2 py-0.5 rounded border border-cyan-500/20">
                                {item.id}
                              </span>
                              <span className="text-[9px] font-mono text-purple-300 font-bold bg-purple-950/30 px-2 py-0.5 rounded border border-purple-500/10">
                                {item.type}
                              </span>
                            </div>
                            <span className={`px-2 py-0.5 rounded text-[8px] font-mono uppercase font-black border ${
                              item.status === "Approved"
                                ? "bg-emerald-950/50 border-emerald-500/30 text-emerald-300"
                                : item.status.includes("Pending")
                                ? "bg-amber-950/40 border-amber-500/20 text-amber-300 animate-pulse"
                                : "bg-rose-950/40 border-rose-500/20 text-rose-300"
                            }`}>
                              {item.status}
                            </span>
                          </div>

                          <h4 className="text-sm font-sans font-bold text-white uppercase tracking-tight mb-2">
                            {item.title}
                          </h4>

                          <p className="text-zinc-400 text-[11px] leading-relaxed line-clamp-2 mb-3">
                            {item.caption}
                          </p>

                          <div className="p-2.5 bg-black/60 rounded-xl border border-white/5 text-[10px] font-mono text-zinc-400 mb-3 space-y-1">
                            <div>Platform Target: <span className="text-white font-bold">{item.platform}</span></div>
                            <div>System Comments: <span className="text-purple-300 italic">“{item.comments}”</span></div>
                          </div>

                          {/* Holographic action buttons */}
                          <div className="flex flex-wrap gap-2 pt-2 border-t border-white/5" onClick={(e) => e.stopPropagation()}>
                            {item.status !== "Approved" ? (
                              <>
                                <button
                                  id={`btn-approve-direct-${item.id}`}
                                  onClick={() => handleApproveItem(item.id)}
                                  className="flex-1 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-black font-sans font-black text-[9px] uppercase tracking-wider rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1.5 shadow-[0_0_10px_rgba(16,185,129,0.15)]"
                                >
                                  <Check size={11} className="stroke-[3px]" />
                                  <span>Sign Off Stamp</span>
                                </button>
                                <button
                                  id={`btn-revision-direct-${item.id}`}
                                  onClick={() => {
                                    const note = prompt("Enter specific revision requirements:");
                                    if (note) {
                                      handleRequestChanges(item.id, note);
                                    }
                                  }}
                                  className="py-1.5 px-3 bg-rose-950/40 hover:bg-rose-900 border border-rose-500/20 text-rose-300 font-sans font-bold text-[9px] uppercase tracking-wider rounded-lg transition-all cursor-pointer"
                                >
                                  Request Revision
                                </button>
                              </>
                            ) : (
                              <div className="flex items-center gap-1.5 text-emerald-400 font-mono text-[9px] uppercase tracking-widest font-black py-1">
                                <CheckCircle2 size={12} className="text-emerald-400 animate-pulse" />
                                <span>SECURE HASH VERIFIED & REPLICATED [0.8s]</span>
                              </div>
                            )}
                            <button
                              id={`btn-load-holographic-${item.id}`}
                              onClick={() => {
                                setSelectedPreviewAd({
                                  id: item.id,
                                  title: item.title,
                                  platform: item.platform.includes("LinkedIn") ? "LinkedIn" : item.platform.includes("YouTube") ? "YouTube" : "Instagram",
                                  previewType: "feed",
                                  avatar: item.id === "APP-001" ? "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=256" : "https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=256",
                                  body: item.caption,
                                  mediaUrl: item.playbackUrl,
                                  likes: item.id === "APP-001" ? 384 : 142,
                                  comments: item.id === "APP-001" ? 42 : 11,
                                  clicks: item.id === "APP-001" ? 1240 : 490,
                                  ctr: "94% CTR calibration sweep"
                                });
                                triggerToast(`Loaded ${item.id} preview calibration matrix`, "success");
                              }}
                              className="px-2 py-1.5 bg-zinc-900 hover:bg-zinc-800 border border-white/5 rounded-lg text-zinc-400 hover:text-white transition flex items-center justify-center gap-1 text-[9px] font-mono"
                            >
                              <Eye size={10} />
                              <span>Inspect Preview</span>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Right Column (5 cols): Dynamic Ad Preview Panel with Auto-Scaling frame */}
                  <div className="lg:col-span-5 space-y-4">
                    <div className="bg-[#0b0518] rounded-2xl border border-cyan-500/20 p-4 relative overflow-hidden flex flex-col justify-between min-h-[580px] shadow-[0_0_15px_rgba(0,229,255,0.05)]">
                      {/* Grid Scanning overlay */}
                      <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/[0.02] via-transparent to-transparent pointer-events-none" />
                      
                      {/* Holographic header scanline */}
                      <div className="absolute top-0 left-0 w-full h-[180px] bg-[linear-gradient(to_bottom,rgba(0,229,255,0.15)_1px,transparent_1px)] [background-size:100%_4px] pointer-events-none opacity-40 animate-[pulse_4s_infinite]" />
                      
                      {/* Header controls bar */}
                      <div className="relative border-b border-white/5 pb-2.5 flex items-center justify-between text-left">
                        <div className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-ping" />
                          <span className="text-[9.5px] font-mono text-[#00e5ff] font-black uppercase tracking-wider">
                            NexoraX Ad Previewer
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => {
                              setAutoScale(!autoScale);
                              triggerToast(`Toggled Scaled Matrix: ${!autoScale ? "Fit Mode" : "Auto Scale"}`, "info");
                            }}
                            className="p-1 px-1.5 text-[8.5px] font-mono bg-cyan-950/60 border border-cyan-500/20 text-cyan-300 rounded hover:bg-cyan-900"
                            title="Auto scale preview container"
                          >
                            SCALE: {autoScale ? "AUTO" : "FIT"}
                          </button>
                        </div>
                      </div>

                      {/* Simulated Interactive Mobile Viewport with scale transform */}
                      <div className="relative flex-1 flex items-center justify-center py-4 overflow-hidden">
                        
                        <div 
                          className="transition-all duration-300 relative border border-white/10 rounded-[30px] p-2 bg-[#0d0717] w-full max-w-[280px] shadow-[0_10px_35px_rgba(0,0,0,0.8),inset_0_1px_5px_rgba(255,255,255,0.1)] flex flex-col"
                          style={{
                            transform: autoScale ? "scale(1.0)" : "scale(0.85)",
                            minHeight: "410px"
                          }}
                        >
                          {/* Notch */}
                          <div className="absolute top-1 left-1/2 -translate-x-1/2 h-3.5 w-24 bg-black rounded-b-xl border-x border-b border-white/5 flex items-center justify-center pointer-events-none">
                            <span className="w-1.5 h-1.5 rounded-full bg-cyan-500/40 mr-1 animate-pulse" />
                            <span className="w-1 h-1 rounded-full bg-zinc-800" />
                          </div>

                          {/* Outer Hologram scanning line and overlay */}
                          <div className="absolute top-0 left-0 w-full h-[1.5px] bg-[#00e5ff] opacity-40 pointer-events-none animate-[scan_2s_linear_infinite]" />

                          {/* Live preview format content container */}
                          {getFilteredSelectedPreviewAd() ? (() => {
                            const previewAd = getFilteredSelectedPreviewAd();
                            return (
                              <div className="flex-1 flex flex-col justify-between text-left p-2.5 pt-4 text-[10.5px] select-none text-zinc-300 relative font-sans">
                                {/* Top Profile Header */}
                                <div className="flex items-center gap-1.5 pb-2 border-b border-white/5">
                                  <img
                                    src={previewAd.avatar}
                                    alt="Eleanor profile"
                                    className="w-6 h-6 rounded-full border border-purple-500/30 object-cover"
                                    referrerPolicy="no-referrer"
                                  />
                                  <div className="text-left flex-1 min-w-0">
                                    <div className="text-white font-bold text-[9.5px] leading-none truncate uppercase flex items-center gap-1">
                                      <span>{role === "Client" ? "Brand Campaign User" : "Eleanor Vance"}</span>
                                      <span className="text-[7px] font-mono text-cyan-400 bg-cyan-950 px-1 py-0.2 rounded font-black">
                                        {role === "Client" ? "LIVE" : "Secure Invoice Register"}
                                      </span>
                                    </div>
                                    <div className="text-zinc-500 text-[8px] font-mono mt-0.5">
                                      {role === "Client" ? brandName : "NexoraX Digital • Sponsor License"}
                                    </div>
                                  </div>
                                  <span className="text-cyan-400 font-mono text-[8px]">{previewAd.platform} Ad</span>
                                </div>

                                {/* Body post text */}
                                <div className="py-2 flex-1 overflow-y-auto max-h-[120px] text-[9.5px] text-zinc-300 leading-tight">
                                  <p className="line-clamp-4">{previewAd.body}</p>
                                </div>

                                {/* Graphic media viewport */}
                                {previewAd.mediaUrl ? (
                                  <div className="relative h-28 rounded-lg overflow-hidden border border-white/5 my-1.5 group/media bg-black">
                                    <img
                                      src={previewAd.mediaUrl}
                                      alt="Live ad preview space"
                                      className="w-full h-full object-cover opacity-90 group-hover/media:scale-105 transition"
                                      referrerPolicy="no-referrer"
                                    />
                                    <div className="absolute top-1 right-1 bg-black/80 border border-white/15 rounded px-1.5 text-[6.5px] text-zinc-400 uppercase font-mono tracking-widest font-bold">
                                      Veo GPU Render
                                    </div>
                                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-1.5 flex justify-between items-center">
                                      <span className="text-[7.5px] text-cyan-300 font-mono font-bold">
                                        {role === "Client" ? "High Performance Content" : "Direct Secure Payment Provider Access Route"}
                                      </span>
                                      <span className="text-[7.5px] text-white font-bold bg-[#e840f3] px-1 rounded uppercase">Learn More</span>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="h-28 rounded-lg bg-zinc-950/90 border border-dashed border-white/10 my-1.5 flex flex-col items-center justify-center text-center p-2">
                                    <Eye size={16} className="text-[#00e5ff] mb-1 animate-pulse" />
                                    <span className="text-[8px] uppercase tracking-wider text-zinc-500 font-mono font-bold">NexoraX Article Post Copy</span>
                                  </div>
                                )}

                                {/* Interactive Social indicators bar */}
                                <div className="border-y border-white/5 py-1.5 flex justify-between items-center text-[7.5px] font-mono text-zinc-500">
                                  <button 
                                    onClick={() => {
                                      setSelectedPreviewAd({ ...selectedPreviewAd, likes: selectedPreviewAd.likes + 1 });
                                      triggerToast(`Incremented simulated engagement calibration`, "info");
                                    }}
                                    className="flex items-center gap-1 hover:text-white transition cursor-pointer"
                                  >
                                    <span>👍 Likes: <strong className="text-white">{previewAd.likes}</strong></span>
                                  </button>
                                  <span>💬 Comments: {previewAd.comments}</span>
                                  <span>🎯 Clicks: {previewAd.clicks}</span>
                                </div>

                                {/* Action conversion box */}
                                <div className="pt-2 bg-gradient-to-r from-purple-950/20 to-cyan-950/10 rounded-lg border border-purple-500/10 p-1.5 text-center flex items-center justify-between mt-auto">
                                  <span className="text-[7.5px] text-zinc-500 uppercase tracking-widest font-mono">Calibrated Engagement index:</span>
                                  <span className="text-[#00e5ff] font-mono text-[8px] font-black">{previewAd.ctr}</span>
                                </div>
                              </div>
                            );
                          })() : (
                            <div className="flex-1 flex flex-col items-center justify-center text-center p-4">
                              <Sparkles size={24} className="text-[#e840f3] mb-2 animate-bounce" />
                              <span className="text-[9px] uppercase tracking-widest text-[#00e5ff] font-mono font-bold block mb-1">
                                Awaiting Node Load
                              </span>
                              <p className="text-zinc-500 text-[10px] leading-relaxed max-w-[180px]">
                                Click "Inspect Preview" on any approval queue element above to calibrate the Hologram ad monitor.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Footer approval UX actions inside preview panel */}
                      <div className="mt-auto border-t border-white/5 pt-3.5 space-y-2 text-left">
                        <div className="flex justify-between items-center text-[9px] font-mono text-zinc-400">
                          <span>Secure Stamp Hash: [SHA-256 SC887095]</span>
                          <span className="text-cyan-400 font-bold">0.8s Engine ready</span>
                        </div>
                        {selectedPreviewAd ? (
                          <div className="flex gap-2">
                            <button
                              id="btn-preview-signoff"
                              onClick={() => {
                                handleApproveItem(selectedPreviewAd.id);
                              }}
                              className="flex-1 py-1.5 bg-gradient-to-r from-cyan-400 to-emerald-400 text-black font-sans font-black text-[9px] uppercase tracking-widest rounded-xl transition cursor-pointer flex items-center justify-center gap-1 shadow-[0_0_10px_rgba(34,211,238,0.2)]"
                            >
                              <CheckCircle2 size={11} />
                              <span>Authorize Direct Broadcast</span>
                            </button>
                            <button
                              onClick={() => {
                                const cmt = prompt("Enter specific calibration instructions:");
                                if (cmt) {
                                  handleRequestChanges(selectedPreviewAd.id, cmt);
                                }
                              }}
                              className="px-2.5 py-1.5 bg-zinc-950 border border-white/10 text-zinc-400 hover:text-white rounded-xl text-[9px] font-mono text-center uppercase"
                            >
                              Revise
                            </button>
                          </div>
                        ) : null}
                      </div>

                    </div>
                  </div>

                </div>

              </div>
            </div>
          )}

          {/* === 5. CAMPAIGN TIMELINE === */}
          {activeTab === "timeline" && (
            <div className="bg-black/70 rounded-3xl border border-purple-500/20 p-6 space-y-6 text-left">
              <div className="space-y-1 border-b border-white/5 pb-3">
                <h3 className="text-xl font-sans font-black text-white uppercase tracking-tight">
                  Holographic Campaign Progress Timeline
                </h3>
                <p className="text-zinc-400 text-xs">Observe live operational tracking sequences as active requests trigger successive rendering nodes.</p>
              </div>

              {/* TIMELINE SEGMENTS FLOW */}
              <div className="relative border-l border-cyan-500/20 ml-4 pl-6 space-y-6 font-mono text-xs">
                {timelineWorkflow.map((node, index) => {
                  const isCompleted = node.status === "completed";
                  const isCurrent = node.status === "current";
                  return (
                    <div key={index} className="relative text-left">
                      {/* Node point marker */}
                      <span className={`absolute -left-[31px] top-0.5 w-4 h-4 rounded-full border ${
                        isCompleted 
                          ? "bg-gradient-to-r from-purple-500 to-cyan-400 border-cyan-400 animate-pulse shadow-[0_0_8px_#00e5ff]"
                          : isCurrent
                          ? "bg-[#e840f3] border-white animate-ping"
                          : "bg-zinc-950 border-white/10"
                      }`} />
                      
                      {isCurrent && (
                        <span className="absolute -left-[31px] top-0.5 w-4 h-4 rounded-full bg-[#e840f3] border border-white" />
                      )}

                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className={`font-black uppercase tracking-wider text-[11px] ${
                            isCompleted ? "text-[#00e5ff]" : isCurrent ? "text-[#e840f3]" : "text-zinc-500"
                          }`}>
                            {index + 1}. {node.name}
                          </span>
                          <span className="text-[8.5px] text-zinc-500 font-bold font-mono">[{node.date}]</span>
                          {isCurrent && (
                            <span className="bg-purple-950/60 text-[#e840f3] border border-purple-500/35 px-1.5 text-[7.5px] rounded animate-pulse font-bold">
                              ACTIVE CALIBRATOR WORKING
                            </span>
                          )}
                        </div>
                        <p className="text-zinc-450 text-[10.5px] leading-relaxed max-w-xl font-sans">
                          {node.desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* === 6. CAMPAIGN LAUNCHER & MANUAL EXPORT HUB === */}
          {activeTab === "assets" && (
              <div className="bg-black/70 rounded-3xl border border-cyan-500/20 p-6 space-y-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-white/5 pb-4">
                  <div className="text-left space-y-1">
                    <span className="text-[9px] font-mono text-cyan-400 font-black uppercase tracking-widest block">★ NexoraX Campaign Control Hub</span>
                    <h3 className="text-2xl font-sans font-black text-white uppercase tracking-tight">
                      NexoraX Campaign Launcher
                    </h3>
                    <p className="text-zinc-400 text-xs">Expose local final export pack campaign assets, copy drafts, dynamic QR routing, and controlled test triggers.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[8.5px] font-mono bg-cyan-950 text-cyan-300 px-2.5 py-1 rounded border border-cyan-500/35 font-bold uppercase">
                      Ready for Manual Launch
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 text-left">
                  {/* Left Column: Selector & Details (5 cols) */}
                  <div className="lg:col-span-5 space-y-5">
                    {/* Campaign Selector */}
                    <div className="space-y-1.5">
                      <label className="text-[9.5px] font-mono uppercase text-cyan-400 font-black tracking-widest block">Select Target Campaign</label>
                      <select 
                        value={selectedCampId} 
                        onChange={(e) => setSelectedCampId(e.target.value)}
                        className="w-full bg-[#050914] border border-slate-800 rounded-xl px-4 py-3 text-xs text-white font-mono focus:border-cyan-500 focus:outline-none"
                      >
                        <option value="DRIVER_FLEET_LAUNCH">Driver / Courier / Fleet Command System</option>
                        <option value="WEBSITE_PACKAGES_LAUNCH">Website Launch Packages</option>
                        <option value="MAINTENANCE_HOSTING_LAUNCH">Hosting & Maintenance Packages</option>
                      </select>
                    </div>

                    {/* Metadata summary */}
                    <div className="bg-[#030712] border border-white/5 rounded-2xl p-5 space-y-3 font-mono text-xs">
                      <div className="flex justify-between border-b border-white/5 pb-2">
                        <span className="text-slate-500">ROUTE TYPE:</span>
                        <span className="text-cyan-400 font-black">{activeSeed.routeType}</span>
                      </div>
                      <div className="space-y-1 border-b border-white/5 pb-2">
                        <span className="text-slate-500 block">DESTINATION URL:</span>
                        <a href={activeSeed.destination} target="_blank" rel="noreferrer" className="text-white hover:text-cyan-300 break-all underline flex items-center gap-1">
                          {activeSeed.destination} <ExternalLink size={10} />
                        </a>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">CONNECTOR STATUS:</span>
                        <span className="text-emerald-400 font-black">ACTIVE</span>
                      </div>
                    </div>

                    {/* Image selector list */}
                    <div className="space-y-2">
                      <label className="text-[9.5px] font-mono uppercase text-slate-500 font-black tracking-widest block">Select Campaign Graphics</label>
                      <div className="grid grid-cols-2 gap-2 max-h-[140px] overflow-y-auto">
                        {campImages.map((imgName) => (
                          <button
                            key={imgName}
                            onClick={() => setSelectedImage(imgName)}
                            className={`p-2 rounded-xl text-left truncate text-[10px] font-mono transition-all border ${
                              selectedImage === imgName 
                                ? "bg-cyan-950/30 border-cyan-500 text-white font-bold" 
                                : "bg-black/40 border-white/5 text-slate-400 hover:border-slate-800 hover:text-white"
                            }`}
                          >
                            {imgName}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* QR Code selector list if any */}
                    {campQRs.length > 0 && (
                      <div className="space-y-2">
                        <label className="text-[9.5px] font-mono uppercase text-slate-500 font-black tracking-widest block">Select Campaign QR Asset</label>
                        <div className="grid grid-cols-2 gap-2">
                          {campQRs.map((qrName) => (
                            <button
                              key={qrName}
                              onClick={() => setSelectedQR(qrName)}
                              className={`p-2 rounded-xl text-left truncate text-[10px] font-mono transition-all border ${
                                selectedQR === qrName 
                                  ? "bg-cyan-950/30 border-cyan-500 text-white font-bold" 
                                  : "bg-black/40 border-white/5 text-slate-400 hover:border-slate-800 hover:text-white"
                              }`}
                            >
                              {qrName}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right Column: Previews & Action Center (7 cols) */}
                  <div className="lg:col-span-7 space-y-6">
                    {/* Media Showcase Row */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Image Preview Card */}
                      <div className="bg-[#030712] border border-white/5 rounded-2xl p-4 flex flex-col justify-between h-[220px]">
                        <span className="text-[9px] font-mono text-slate-500 uppercase tracking-widest block mb-2">Campaign Graphic Preview</span>
                        <div className="flex-1 bg-black/40 rounded-xl overflow-hidden flex items-center justify-center border border-white/5 relative group">
                          {selectedImage ? (
                            <img 
                              src={`/api/campaign-assets/file/${selectedCampId}/${selectedImage}`} 
                              alt="Selected Graphic" 
                              className="w-full h-full object-cover group-hover:scale-105 transition-all duration-300"
                            />
                          ) : (
                            <span className="text-[10px] text-slate-600 font-mono">No Image Selected</span>
                          )}
                        </div>
                        {selectedImage && (
                          <a 
                            href={`/api/campaign-assets/file/${selectedCampId}/${selectedImage}`} 
                            download={selectedImage}
                            className="mt-3 py-2 bg-cyan-950 hover:bg-cyan-900 border border-cyan-500/30 text-cyan-300 font-mono text-[9px] uppercase font-black rounded-lg transition-all flex items-center justify-center gap-1.5"
                          >
                            <Download size={11} /> Download Image
                          </a>
                        )}
                      </div>

                      {/* QR Preview Card */}
                      <div className="bg-[#030712] border border-white/5 rounded-2xl p-4 flex flex-col justify-between h-[220px]">
                        <span className="text-[9px] font-mono text-slate-500 uppercase tracking-widest block mb-2">Tracked QR Code Preview</span>
                        <div className="flex-1 bg-black/40 rounded-xl overflow-hidden flex items-center justify-center border border-white/5 relative p-4">
                          {selectedQR ? (
                            <img 
                              src={`/api/campaign-assets/file/${selectedCampId}/${selectedQR}`} 
                              alt="Selected QR Code" 
                              className="max-h-full object-contain filter invert opacity-90"
                            />
                          ) : (
                            <span className="text-[10px] text-slate-600 font-mono">No QR Associated</span>
                          )}
                        </div>
                        {selectedQR && (
                          <a 
                            href={`/api/campaign-assets/file/${selectedCampId}/${selectedQR}`} 
                            download={selectedQR}
                            className="mt-3 py-2 bg-cyan-950 hover:bg-cyan-900 border border-cyan-500/30 text-cyan-300 font-mono text-[9px] uppercase font-black rounded-lg transition-all flex items-center justify-center gap-1.5"
                          >
                            <Download size={11} /> Download QR Code
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Copy Text Area Tabs */}
                    <div className="bg-[#030712] border border-white/5 rounded-2xl p-5 space-y-4">
                      <div className="flex justify-between items-center border-b border-white/5 pb-2">
                        <span className="text-[9px] font-mono text-slate-400 font-black uppercase tracking-widest">Manual Channel Copy Deck</span>
                        <span className="text-[8.5px] font-mono text-cyan-400 uppercase font-bold bg-cyan-950/30 px-2 py-0.5 rounded">Manual Export Only</span>
                      </div>

                      {/* Social Grid Previews */}
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { key: "LinkedIn", title: "LinkedIn B2B", text: activeSeed.linkedinCopy },
                          { key: "Facebook", title: "Facebook Ad", text: activeSeed.facebookCopy },
                          { key: "WhatsApp", title: "WhatsApp Info", text: activeSeed.whatsappCopy }
                        ].map((net) => (
                          <button
                            key={net.key}
                            onClick={() => {
                              navigator.clipboard.writeText(net.text);
                              triggerToast(`Copied ${net.key} campaign copy to clipboard!`, "success");
                            }}
                            className="py-2.5 bg-black/50 border border-white/5 hover:border-cyan-500/40 text-slate-300 hover:text-white rounded-xl text-[10px] font-mono font-bold transition-all uppercase flex flex-col items-center justify-center gap-1 cursor-pointer"
                          >
                            <span>{net.title}</span>
                            <span className="text-[8px] text-cyan-400 lowercase">click to copy</span>
                          </button>
                        ))}
                      </div>

                      {/* Preview Copy block */}
                      <div className="p-3 bg-black/60 border border-slate-900 rounded-xl max-h-[100px] overflow-y-auto font-mono text-[10px] text-zinc-400 leading-relaxed scrollbar-thin">
                        <div className="text-[9px] text-slate-500 border-b border-white/5 pb-1 mb-1.5 uppercase font-bold">LinkedIn Launch Post Preview:</div>
                        <p>{activeSeed.linkedinCopy}</p>
                      </div>
                    </div>

                    {/* Unified Trigger Command Center */}
                    <div className="bg-[#050c18]/85 border border-cyan-500/20 rounded-2xl p-5 space-y-4">
                      <span className="text-[9.5px] font-mono text-cyan-400 font-black uppercase tracking-widest block border-b border-white/5 pb-2">
                        Accelerated Trigger Suite (Safe Staging)
                      </span>

                      <div className="grid grid-cols-2 gap-3">
                        {/* Queue Task */}
                        <div className="space-y-1.5">
                          <button
                            onClick={handleQueueTask}
                            disabled={taskQueueStatus === "queuing"}
                            className="w-full py-3 bg-indigo-650 hover:bg-indigo-600 border border-indigo-500/30 text-white font-sans font-black text-[10px] uppercase tracking-widest rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
                          >
                            <Workflow size={11} /> 
                            <span>{taskQueueStatus === "queuing" ? "Queueing Task..." : taskQueueStatus === "queued" ? "Task Backlogged" : "Queue Follow-Up Task"}</span>
                          </button>
                          <span className="text-[8px] font-mono text-slate-500 block text-center uppercase">Appends campaign mandate to SQLite nexorax_tasks</span>
                        </div>

                        {/* Send Test Email */}
                        <div className="space-y-1.5">
                          <button
                            onClick={() => {
                              if (!testEmailSentThisSession) {
                                setShowConfirmModal(true);
                              }
                            }}
                            disabled={sendingTestEmail || testEmailSentThisSession}
                            className="w-full py-3 bg-cyan-400 hover:bg-cyan-300 text-black font-sans font-black text-[10px] uppercase tracking-widest rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
                          >
                            <Send size={11} /> 
                            <span>
                              {sendingTestEmail 
                                ? "Sending Test..." 
                                : testEmailSentThisSession 
                                  ? "Test email already sent this session" 
                                  : "Send Test Email"}
                            </span>
                          </button>
                          <span className="text-[8px] font-mono text-slate-500 block text-center uppercase">Dispatches pitch to configured test email only</span>
                        </div>

                        {showConfirmModal && (
                          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
                            <div className="bg-[#090f27] border border-purple-500/40 shadow-[0_0_30px_rgba(168,76,255,0.25)] rounded-2xl max-w-[450px] w-full p-6 text-left font-mono">
                              <h3 className="text-white text-sm font-black uppercase tracking-wider mb-3 text-purple-400">Confirm Test Email</h3>
                              <p className="text-slate-400 text-xs leading-relaxed mb-6">This will send ONE test email only to the configured admin/test email address. It will not send to customers. Continue?</p>
                              <div className="flex gap-3 justify-end">
                                <button 
                                  onClick={() => setShowConfirmModal(false)}
                                  className="bg-white/5 border border-white/10 hover:bg-white/10 text-slate-400 px-4.5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest cursor-pointer transition-all"
                                >
                                  Cancel
                                </button>
                                <button 
                                  onClick={() => {
                                    setShowConfirmModal(false);
                                    handleSendTestEmail();
                                  }}
                                  className="bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white shadow-[0_0_15px_rgba(168,76,255,0.3)] hover:opacity-90 px-4.5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest cursor-pointer transition-all"
                                >
                                  Send One Test Email
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Log state bar */}
                      <div className="p-3 bg-black/60 border border-slate-900 rounded-xl font-mono text-[9px] flex justify-between items-center">
                        <div className="flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                          <span className="text-slate-400">TEST MAIL DISPATCH STATUS:</span>
                        </div>
                        <span className="text-white font-bold uppercase">{emailStatus}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

          {/* === 7. PUBLISHING CALENDAR === */}
          {activeTab === "calendar" && (
            <div className="bg-black/70 rounded-3xl border border-purple-500/20 p-6 space-y-4 text-left">
              <div className="space-y-1 border-b border-white/5 pb-3">
                <h3 className="text-xl font-sans font-black text-white uppercase tracking-tight">
                  Automated Content Publishing Calendar
                </h3>
                <p className="text-zinc-400 text-xs">Visualize timed launch intervals dispatched directly to YouTube, LinkedIn, TikTok, and general blog frameworks.</p>
              </div>

              <div className="space-y-3 font-mono text-xs">
                {getFilteredCalendarEvents().map((item, idx) => (
                  <div key={idx} className="bg-zinc-950/70 p-4 rounded-xl border border-white/5 flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 bg-gradient-to-br from-purple-950/60 to-cyan-950/60 rounded-xl border border-white/10 text-center min-w-[75px]">
                        <span className="text-[11px] font-black text-[#00e5ff] block uppercase leading-none">{item.platform}</span>
                        <span className="text-[8px] text-zinc-500 font-black block mt-1">{item.time}</span>
                      </div>
                      <div className="text-left space-y-1">
                        <h4 className="text-sm font-sans font-bold text-white uppercase tracking-tight">
                          {item.title}
                        </h4>
                        <p className="text-[10px] text-zinc-400">Scheduled Broadcast: <strong className="text-white">{item.date}</strong></p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 self-stretch md:self-auto justify-between md:justify-end border-t border-white/5 md:border-transparent pt-2 md:pt-0">
                      <span className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase ${
                        item.status === "Approved"
                          ? "bg-emerald-900/30 border border-emerald-500/20 text-emerald-300"
                          : item.status.includes("Review")
                          ? "bg-amber-900/30 border border-amber-500/20 text-amber-300"
                          : "bg-rose-900/30 border border-rose-500/20 text-rose-300"
                      }`}>
                        {item.status}
                      </span>
                      <button
                        onClick={() => triggerToast(`Rescheduling ${item.title} timed trigger buffer...`, "info")}
                        className="px-2.5 py-1.5 bg-zinc-900 border border-white/10 rounded-lg text-[9px] uppercase hover:bg-white/5 cursor-pointer text-white"
                      >
                        Reschedule
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* === 8. PERFORMANCE DASHBOARD === */}
          {activeTab === "performance" && (
            <div className="space-y-6">
              <div className="bg-black/70 rounded-3xl border border-purple-500/20 p-6 space-y-6 text-left">
                <div className="flex justify-between items-center border-b border-white/5 pb-3">
                  <div className="space-y-1">
                    <h3 className="text-xl font-sans font-black text-white uppercase tracking-tight">
                      NexoraX Analytics Dashboard
                    </h3>
                    <p className="text-zinc-400 text-xs">Verify continuous high-converting performance vectors and telemetry validation loops.</p>
                  </div>
                  <span className="text-[8.5px] font-mono bg-cyan-950/60 text-[#00e5ff] px-2 py-0.5 rounded border border-cyan-500/15 uppercase font-bold tracking-widest">
                    CTR CALIBRATION SWEEP
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 font-mono text-xs text-left">
                  <div className="p-4 bg-zinc-950/70 rounded-2xl border border-cyan-500/10 space-y-1.5">
                    <span className="text-zinc-500 uppercase tracking-widest font-black block text-[9.5px]">Active Leads Pool</span>
                    <h4 className="text-2xl font-black text-[#00e5ff]">{performanceData.leads}</h4>
                    <p className="text-[10px] text-zinc-450 font-sans">Accumulated under SecurePayment sweep telemetry</p>
                  </div>
                  <div className="p-4 bg-zinc-950/70 rounded-2xl border border-purple-500/10 space-y-1.5">
                    <span className="text-zinc-500 uppercase tracking-widest font-black block text-[9.5px]">Avg CTR Metric</span>
                    <h4 className="text-2xl font-black text-purple-300">{performanceData.ctr}</h4>
                    <p className="text-[10px] text-zinc-450 font-sans">0.8s resolution drift sweep</p>
                  </div>
                  <div className="p-4 bg-zinc-950/70 rounded-2xl border border-white/5 space-y-1.5">
                    <span className="text-zinc-500 uppercase tracking-widest font-black block text-[9.5px]">Conversion Success Rate</span>
                    <h4 className="text-2xl font-black text-white">{performanceData.conversion}</h4>
                    <p className="text-[10px] text-zinc-450 font-sans">Authorized candidate acquisition percentage</p>
                  </div>
                  <div className="p-4 bg-zinc-950/70 rounded-2xl border border-emerald-500/10 space-y-1.5 animate-pulse">
                    <span className="text-zinc-500 uppercase tracking-widest font-black block text-[9.5px]">Net Yield ROI Target</span>
                    <h4 className="text-2xl font-black text-emerald-400">{performanceData.roiEst}</h4>
                    <p className="text-[10px] text-zinc-450 font-sans">Tuned against resource goals</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-2">
                  <div className="bg-zinc-950 p-5 rounded-2xl border border-white/5 space-y-3.5 text-xs flex flex-col justify-between">
                    <div>
                      <h4 className="text-[10.5px] uppercase font-bold text-[#00e5ff] tracking-widest flex items-center gap-1.5 mb-3">
                        <Sparkles size={11} className="text-[#00e5ff] animate-pulse" />
                        AI Attribution report (Node-06 Oracle Calibration)
                      </h4>
                      <div className="space-y-2.5 text-[11px] leading-relaxed">
                        <div className="flex justify-between border-b border-white/5 pb-1.5">
                          <span className="text-zinc-450">Top Performing NexoraX Platform:</span>
                          <strong className="text-[#00e5ff] font-mono">{performanceData.bestPlatform}</strong>
                        </div>
                        <div className="flex justify-between border-b border-white/5 pb-1.5 font-sans">
                          <span className="text-zinc-400">High-Converting Ad Segment:</span>
                          <strong className="text-white font-sans">{performanceData.bestVideo}</strong>
                        </div>
                        <div className="flex justify-between pb-1.5 font-sans">
                          <span className="text-zinc-400">Persuasive Master Hook Trigger:</span>
                          <strong className="text-purple-300 font-sans italic">{performanceData.bestHook}</strong>
                        </div>
                      </div>
                    </div>
                    <div className="p-3 bg-cyan-950/20 rounded-xl border border-cyan-500/10 text-[9.5px] font-mono leading-normal text-cyan-300">
                      <strong>AI CALIBRATION SUMMARY:</strong> CTR is running at {performanceData.ctr} representing a {performanceData.conversion} conversion multiplier on @nexorax.co.uk domain vectors.
                    </div>
                  </div>

                  {/* HIGH-FIDELITY TRENDING KEYWORD INTELLIGENCE DASHBOARD */}
                  <div className="bg-[#0c061a] p-5 rounded-2xl border border-purple-500/20 space-y-4 text-xs text-left relative overflow-hidden">
                    <div className="flex justify-between items-center border-b border-white/5 pb-2">
                      <h4 className="text-[10.5px] uppercase font-bold text-white tracking-widest flex items-center gap-1.5">
                        <TrendingUp size={12} className="text-[#e840f3]" />
                        Campaign Search Trends (Node-09 Content Intelligence)
                      </h4>
                      <span className="text-[8px] font-mono text-purple-300 bg-purple-950/60 px-1.5 py-0.5 rounded uppercase font-black tracking-widest animate-pulse">
                        LIVE INDEX SYNC
                      </span>
                    </div>

                    <div className="space-y-2 max-h-[160px] overflow-y-auto pr-1">
                      {trendStats.map((trend, index) => {
                        const isActive = activeTrendTag === trend.tag;
                        return (
                          <div 
                            key={index} 
                            onClick={() => {
                              setActiveTrendTag(trend.tag);
                              triggerToast(`Acquiring high-conversion context for tag: ${trend.tag}`, "success");
                            }}
                            className={`p-2 rounded-xl border transition-all duration-300 flex justify-between items-center cursor-pointer ${
                              isActive 
                                ? "bg-gradient-to-r from-purple-950/60 to-black border-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.1)]" 
                                : "bg-black/40 border-white/5 hover:border-white/10"
                            }`}
                          >
                            <div className="space-y-0.5 text-left">
                              <span className="text-[11.5px] font-sans font-black text-white block truncate max-w-[170px]">
                                {trend.tag}
                              </span>
                              <div className="flex gap-2 text-[8px] font-mono text-zinc-500">
                                <span>CTR TARGET: <strong className="text-cyan-400">{trend.ctrTarget}</strong></span>
                                <span>Difficulty: <strong className={trend.difficulty === "Low" ? "text-emerald-400" : trend.difficulty === "High" ? "text-rose-400" : "text-amber-400"}>{trend.difficulty}</strong></span>
                              </div>
                            </div>
                            <div className="text-right">
                              <span className="text-[9.5px] font-mono font-bold text-zinc-300 block">{trend.searches} Index lookup</span>
                              <span className="text-[8px] font-mono text-rose-400 font-bold block">{trend.change}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Calibrated trigger action */}
                    <div className="p-2.5 bg-black/60 rounded-xl border border-white/5 flex items-center justify-between text-[9px] font-mono mt-1 font-sans">
                      <span className="text-zinc-500">Selected Calibration Seed:</span>
                      <button 
                        onClick={() => {
                          triggerToast(`Synthesized campaign template targets set to: ${activeTrendTag}`, "success");
                          setActiveTab("request");
                        }}
                        className="text-[#00e5ff] font-bold uppercase tracking-wider flex items-center gap-1 hover:underline"
                      >
                        <span>🎯 TARGET: {activeTrendTag} (Clone Template)</span>
                        <ArrowUpRight size={10} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* === 9. BILLING & USAGE === */}
          {activeTab === "billing" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-black/70 rounded-3xl border border-purple-500/20 p-6 space-y-6">
                <div className="flex justify-between items-center border-b border-white/5 pb-3">
                  <div className="space-y-1 text-left">
                    <h3 className="text-xl font-sans font-black text-white uppercase tracking-tight">
                      {role === "Client" ? "SaaS Subscription & Invoices" : "Transactional Command Ledger"}
                    </h3>
                    <p className="text-zinc-400 text-xs">
                      {role === "Client"
                        ? "Manage your active pricing model, credit consumption thresholds, and direct Stripe invoice receipts."
                        : "Zero-Fund Services Guide direct route & SecurePayment settlement reconciliation trackers."}
                    </p>
                  </div>
                  <span className="text-[8.5px] font-mono bg-purple-950 text-purple-300 px-2 py-0.5 rounded border border-purple-500/30 uppercase font-black tracking-widest animate-pulse">
                    {role === "Client" ? "STRIPE SECURE" : "MANDATE STRICT"}
                  </span>
                </div>

                <div className="space-y-3 text-xs leading-relaxed font-sans text-zinc-300">
                  {/* Gated direct billing headers */}
                  {role === "Client" ? (
                    <div className="p-4 bg-[#0a0518]/80 border border-purple-500/20 rounded-2xl flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                      <div className="space-y-1 text-left">
                        <span className="text-[8.5px] font-mono font-bold text-[#00e5ff] uppercase block">ACTIVE CLIENT BILLING GATEWAY</span>
                        <h4 className="text-sm font-bold text-white uppercase">NexoraX Pro Multi-Platform License</h4>
                        <p className="text-[10px] text-zinc-400 font-mono">Isolated Workspace Session: SEC-8b1d48cf-525c</p>
                      </div>
                      <div className="text-right">
                        <span className="text-xs text-zinc-500 block">Upcoming SaaS Bill Date</span>
                        <span className="text-xl font-mono font-black text-cyan-400">June 20, 2026</span>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 bg-zinc-950/70 border border-white/5 rounded-2xl flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                      <div className="space-y-1 text-left">
                        <span className="text-[8.5px] font-mono font-bold text-zinc-500 uppercase block">DIRECT BILLING HANDSHAKE</span>
                        <h4 className="text-sm font-bold text-white uppercase">NexoraX billing records scheme (NI: NH******B)</h4>
                        <p className="text-[10px] text-zinc-400 font-mono">NexoraX Digital Ltd Sponsor License: SC887095</p>
                      </div>
                      <div className="text-right">
                        <span className="text-xs text-zinc-500 block">Pending Invoice Sweep</span>
                        <span className="text-2xl font-mono font-black text-cyan-400">{pendingBalance}</span>
                      </div>
                    </div>
                  )}

                  {/* Financial NexoraXty private layout - ONLY visible to admins */}
                  {role !== "Client" ? (
                    <div className="p-4 bg-[#110520]/60 border border-purple-500/15 rounded-2xl space-y-3 text-left">
                      <div className="flex items-center gap-2">
                        <Info size={14} className="text-[#e840f3]" />
                        <span className="text-xs font-mono font-black text-white uppercase tracking-wide">FINANCIAL NEXORAXTY (NON-NEGOTIABLE):</span>
                      </div>
                      <p className="text-zinc-350 text-[11px] leading-relaxed">
                        This corporate matrix routing applies the **Tiered Extraction Protocol (1% 3% Community Allocation for Charities, 10% Maintenance & Hosting Fee)** on every transaction. All net settlements route to SecurePayment Vaults securely.
                      </p>
                      <div className="p-3 bg-black/60 rounded-xl border border-white/5 text-[10px] font-mono text-zinc-400 text-left space-y-1">
                        <div className="flex justify-between">
                          <span>Secure Payment Provider Code:</span>
                          <strong className="text-white">04-**-**</strong>
                        </div>
                        <div className="flex justify-between">
                          <span>Reconciliation Account Number:</span>
                          <strong className="text-white">******15</strong>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 bg-[#050b18]/70 border border-cyan-500/15 rounded-2xl space-y-2 text-left">
                      <div className="flex items-center gap-1.5 text-cyan-400">
                        <CheckCircle2 size={13} />
                        <span className="text-xs font-mono font-bold uppercase">Stripe Subscription Secured:</span>
                      </div>
                      <p className="text-zinc-400 text-[11px] leading-relaxed">
                        All transactions are processed through encrypted Stripe endpoints. Customer invoice reports and corporate ledger lines are isolated per brand profiles for maximum compliance safety.
                      </p>
                    </div>
                  )}
                </div>

                {/* --- NEXORAX BILLING ANALYTICS & PAYMENT TIERS GRID --- */}
                <div className="border-t border-white/5 pt-5 space-y-4 text-left">
                  <div className="flex justify-between items-center border-b border-white/5 pb-2">
                    <h4 className="text-xs font-mono font-black text-cyan-300 uppercase tracking-widest flex items-center gap-1.5">
                      <Sliders size={12} className="text-[#00e5ff]" />
                      NexoraX Payment Tiers & Billing Analytics
                    </h4>
                    <span className="text-[8.5px] font-mono text-zinc-500 uppercase">CALCULATED ROI ESTIMATION Matrix</span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    
                    {/* Tier 1: Bronze Starter */}
                    <div className="p-4 bg-zinc-950/70 border border-white/5 rounded-2xl flex flex-col justify-between hover:border-purple-500/20 transition group text-left">
                      <div className="space-y-2">
                        <div className="flex justify-between items-start">
                          <span className="text-[9px] font-mono text-purple-300 font-bold bg-purple-950/40 px-2 py-0.5 rounded border border-purple-500/20 uppercase">Tier 01</span>
                          <span className="text-[9.5px] font-mono text-zinc-500">Bronze</span>
                        </div>
                        <h4 className="text-sm font-sans font-black text-white uppercase mt-1">Starter Matrix</h4>
                        <div className="text-xl font-mono font-black text-zinc-100 mt-1">£1,200 <span className="text-[9px] text-zinc-500 font-normal">/mo</span></div>
                        
                        <ul className="text-[10px] text-zinc-400 font-mono space-y-1 pt-1.5 list-inside list-disc">
                          <li>10 Rendered UGC Videos</li>
                          <li>Standard Resolution GPU</li>
                          <li>{role === "Client" ? "Stripe Automated Billing" : "Direct SecurePayment Sweep"}</li>
                        </ul>
                      </div>

                      <div className="pt-3 border-t border-white/5 mt-4 space-y-1.5 font-mono text-[9px]">
                        <div className="flex justify-between">
                          <span className="text-zinc-500">Predicted Clicks:</span>
                          <span className="text-white font-bold">1.2K /mo</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-zinc-500">Estimated Yield:</span>
                          <span className="text-cyan-400 font-black">£3,400 /mo</span>
                        </div>
                        <button
                          onClick={() => triggerToast("Bronze campaign simulator calibrated to active hooks", "info")}
                          className="w-full mt-2 py-1 bg-zinc-900 border border-white/10 text-zinc-400 hover:text-white transition rounded-md text-[8.5px] uppercase font-bold"
                        >
                          Forecast Starter
                        </button>
                      </div>
                    </div>

                    {/* Tier 2: Gold Solver */}
                    <div className="p-4 bg-gradient-to-br from-purple-950/20 to-black border border-purple-500/30 rounded-2xl flex flex-col justify-between hover:border-purple-400/40 transition relative group shadow-[0_0_15px_rgba(168,85,247,0.05)] text-left">
                      <div className="absolute top-2 right-2 flex gap-1">
                        <span className="bg-[#e840f3]/25 text-[#e840f3] border border-[#e840f3]/35 text-[7px] font-mono font-black px-1.5 py-0.2 rounded uppercase animate-pulse">POPULAR</span>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between items-start">
                          <span className="text-[9px] font-mono text-[#e840f3] font-bold bg-[#e840f3]/10 px-2 py-0.5 rounded border border-[#e840f3]/20 uppercase">Tier 02</span>
                          <span className="text-[9.5px] font-mono text-zinc-400">Gold</span>
                        </div>
                        <h4 className="text-sm font-sans font-black text-white uppercase mt-1">Imperial Solver</h4>
                        <div className="text-xl font-mono font-black text-white mt-1">£5,000 <span className="text-[9px] text-zinc-500 font-normal">/mo</span></div>
                        
                        <ul className="text-[10px] text-zinc-300 font-mono space-y-1 pt-1.5 list-inside list-disc">
                          <li>50 Rendered UGC Videos</li>
                          <li>Dual Node-06 Engine Speed</li>
                          <li>{role === "Client" ? "Dedicated Brand SLA" : "1% NexoraX Tax Ledger"}</li>
                        </ul>
                      </div>

                      <div className="pt-3 border-t border-white/5 mt-4 space-y-1.5 font-mono text-[9px]">
                        <div className="flex justify-between">
                          <span className="text-zinc-500">Predicted Clicks:</span>
                          <span className="text-white font-bold">8.5K /mo</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-zinc-500">Estimated Yield:</span>
                          <span className="text-cyan-400 font-black">£19,800 /mo</span>
                        </div>
                        <button
                          onClick={() => triggerToast("Gold Imperial forecast calibrated to active target metrics", "success")}
                          className="w-full mt-2 py-1 bg-[#1c082e] border border-purple-500/20 text-[#e840f3] hover:text-white transition rounded-md text-[8.5px] uppercase font-bold"
                        >
                          Forecast Imperial
                        </button>
                      </div>
                    </div>

                    {/* Tier 3: VIP Resource NexoraX (Or Dedicated Enterprise customized for public) */}
                    <div className="p-4 bg-zinc-950/70 border border-cyan-500/20 rounded-2xl flex flex-col justify-between hover:border-cyan-400/40 transition group text-left">
                      <div className="space-y-2">
                        <div className="flex justify-between items-start">
                          <span className="text-[9px] font-mono text-cyan-300 font-bold bg-cyan-950/40 px-2 py-0.5 rounded border border-cyan-500/20 uppercase">Tier 03</span>
                          <span className="text-[9.5px] font-mono text-zinc-500">NexoraX</span>
                        </div>
                        <h4 className="text-sm font-sans font-black text-white uppercase mt-1">
                          {role === "Client" ? "Dedicated Custom Node" : "Resource Special"}
                        </h4>
                        <div className="text-lg font-mono font-black text-cyan-400 mt-1 truncate">
                          {role === "Client" ? "Enterprise SLA Route" : "Secure Payment Provider Grant Route"}
                        </div>
                        
                        <ul className="text-[10px] text-zinc-400 font-mono space-y-1 pt-1.5 list-inside list-disc">
                          <li>Unlimited Video Renderings</li>
                          <li>{role === "Client" ? "Dedicated GPU Cluster Allocation" : "Direct Resource resource"}</li>
                          <li>@nexorax.co.uk domain</li>
                        </ul>
                      </div>

                      <div className="pt-3 border-t border-white/5 mt-4 space-y-1.5 font-mono text-[9px]">
                        <div className="flex justify-between">
                          <span className="text-zinc-500">Predicted Clicks:</span>
                          <span className="text-white font-bold">Infinite Capacity</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-zinc-500">Estimated Yield:</span>
                          <span className="text-cyan-400 font-black">
                            {role === "Client" ? "High conversion CTR" : "£520K Target"}
                          </span>
                        </div>
                        <button
                          onClick={() => triggerToast(`NexoraX forecast calibrated against ${role === 'Client' ? 'Enterprise' : 'SC887095'} parameters`, "info")}
                          className="w-full mt-2 py-1 bg-cyan-950/40 border border-cyan-500/20 text-cyan-300 hover:text-cyan-100 transition rounded-md text-[8.5px] uppercase font-bold"
                        >
                          Forecast Node
                        </button>
                      </div>
                    </div>

                  </div>
                </div>

                {/* UPGRADE AND STRIPE PAYMENT BUTTON */}
                <div className="pt-4 border-t border-white/5 text-center space-y-3">
                  <p className="text-[11px] text-zinc-400 font-sans">
                    {role === "Client"
                      ? "Secure your high-performance multi-platform campaign limits. Upgrade instantly via our verified Stripe payment integration."
                      : "Authorize the placement validation protocol or secure private ledger capabilities via Lead Director's authorized Stripe network."}
                  </p>
                  <div>
                    <a
                      id="stripe-direct-payment-link"
                      href="https://buy.stripe.com/8x2bIU2t59oYgTtfxQaMU00"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-400 hover:opacity-95 text-black font-sans font-black text-[10px] uppercase tracking-widest rounded-xl transition-all duration-300 hover:scale-[1.02] cursor-pointer inline-flex items-center gap-1.5 shadow-[0_0_20px_rgba(34,211,238,0.2)]"
                    >
                      <CreditCard size={12} />
                      <span>{role === "Client" ? "Upgrade Subscription via Stripe Secure" : "Upgrade Plan / Direct Stripe Payment (£5,100,000 Allocation)"}</span>
                      <ExternalLink size={11} />
                    </a>
                  </div>
                </div>
              </div>

              {/* Sidebar Usage Summary */}
              <div className="bg-[#070411]/85 rounded-3xl border border-white/5 p-5 space-y-4 text-left font-mono text-xs">
                <h4 className="text-xs font-bold text-[#e840f3] uppercase tracking-wider border-b border-white/5 pb-2">
                  Ecosystem Consumption Rates
                </h4>

                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-[11px] text-zinc-400 mb-1">
                      <span>AI Model Credits consumed</span>
                      <span className="text-white font-bold">{creditsUsed} / {creditsTotal}</span>
                    </div>
                    <div className="h-2 bg-zinc-950 border border-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-cyan-400" style={{ width: "84%" }}></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between text-[11px] text-zinc-500 mb-1">
                      <span>Total Videos Rendered</span>
                      <span className="text-white font-bold">{videosGenerated} / 60 Limit</span>
                    </div>
                    <div className="h-2 bg-zinc-950 border border-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-purple-500" style={{ width: "80%" }}></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between text-[11px] text-zinc-500">
                      <span>Campaign Matrix Triggers</span>
                      <span className="text-white font-bold">{campaignsCreated} Generated</span>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-white/5 text-[9.5px] text-zinc-400">
                    <span className="text-amber-400 block font-bold mb-1">★ OVERUSE CAP RISK DETECTED</span>
                    <span>
                      {role === "Client"
                        ? "To expand rendering limit beyond 60 Portrait Videos per cycle, contact NexoraX Support directly via our freephone support frequency."
                        : "To expand render limit beyond 60 Portrait Videos per cycle, contact Lead Director directly on 07359******."}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* === 10. SUPPORT & COLLABORATION === */}
          {activeTab === "support" && (
            <div className="bg-black/70 rounded-3xl border border-purple-500/20 p-6 space-y-4">
              <div className="flex justify-between items-center border-b border-white/5 pb-3 text-left">
                <div className="space-y-1">
                  <h3 className="text-xl font-sans font-black text-white uppercase tracking-tight">
                    Support & Collaboration Portal
                  </h3>
                  <p className="text-zinc-400 text-xs">Establish direct contact on the 08000 freephone, submit revision requests, or audit historical chats.</p>
                </div>
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse inline-block" />
              </div>

              {/* INLINE CONVERSATION */}
              <div className="bg-zinc-950/70 rounded-2xl border border-white/5 p-4 space-y-4 text-xs font-mono text-left max-h-[300px] overflow-y-auto">
                {getFilteredSupportMessages().map((msg, index) => {
                  const isClient = msg.sender.includes("Client");
                  const isManager = msg.sender.includes("Mahmood") || msg.sender.includes("Technical");
                  return (
                    <div
                      key={index}
                      className={`p-3 rounded-2xl border max-w-xl text-left ${
                        isClient
                          ? "bg-purple-950/20 border-purple-500/15 ml-auto"
                          : isManager
                          ? "bg-rose-950/20 border-rose-500/15"
                          : "bg-cyan-950/20 border-cyan-500/15"
                      }`}
                    >
                      <div className="flex justify-between items-center text-[8.5px] font-mono mb-1 text-zinc-500">
                        <span className={`font-black uppercase tracking-wider ${
                          isClient ? "text-[#e840f3]" : isManager ? "text-rose-300" : "text-[#00e5ff]"
                        }`}>
                          {msg.sender}
                        </span>
                        <span>{msg.time}</span>
                      </div>
                      <p className="text-zinc-300 font-sans text-[11px] leading-relaxed">
                        {msg.text}
                      </p>
                    </div>
                  );
                })}
              </div>

              <form onSubmit={handlePostComment} className="flex gap-2">
                <input
                  id="collaboration-message-input"
                  type="text"
                  placeholder="Enter comments, revisions or system request parameters..."
                  className="flex-1 bg-zinc-950 border border-white/10 rounded-xl p-3 text-white text-xs font-sans focus:outline-none focus:border-[#e840f3] transition"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                />
                <button
                  id="btn-post-comment"
                  type="submit"
                  className="px-5 bg-[#e840f3] hover:opacity-90 text-black font-sans font-black text-[10px] uppercase tracking-widest rounded-xl transition cursor-pointer flex items-center justify-center gap-1"
                >
                  <Send size={11} />
                  <span>Send</span>
                </button>
              </form>

              {/* External Routing Actions */}
              <div className="pt-2 border-t border-white/5 flex flex-wrap gap-3 justify-between items-center text-xs font-mono text-zinc-500">
                <div className="flex items-center gap-2">
                  <span>Authorized Support: support@nexoraxdigital.co.uk</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-amber-400 font-bold">Verbal Sync Anchor: 08000 622827</span>
                </div>
              </div>
            </div>
          )}

          {/* === 11. AGENCY CONSOLE === */}
          {activeTab === "agency" && (role === "Agency/Admin" || role === "Super Admin") && (
            <div className="space-y-6 text-left">
              <div className="bg-gradient-to-r from-[#1c0835] to-[#05020c] p-6 rounded-3xl border border-purple-500/30 space-y-4">
                <div className="flex justify-between items-center border-b border-white/5 pb-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded bg-purple-950 border border-purple-500/30 text-[8px] font-mono font-black text-purple-300 uppercase tracking-widest animate-pulse">
                        NexoraX Access Level II
                      </span>
                      <span className="text-zinc-500 text-[10px] font-mono">ID: SEC-NODE-18.5</span>
                    </div>
                    <h3 className="text-xl font-sans font-black text-white uppercase tracking-tight">
                      ★ Agency Campaign & Production Command
                    </h3>
                    <p className="text-zinc-400 text-xs">Acknowledge corporate alignment, manage multi-tenant campaigns, and track remote node resources.</p>
                  </div>
                  <Users className="text-[#e840f3] animate-pulse" size={24} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left Column: Campaigns & Assignments */}
                  <div className="lg:col-span-2 bg-black/60 rounded-2xl border border-white/5 p-5 space-y-4">
                    <h4 className="text-xs font-mono font-black text-purple-400 uppercase tracking-wider flex items-center gap-1.5 border-b border-white/5 pb-2">
                      <Sliders size={12} />
                      Multi-Client Campaign Queue
                    </h4>

                    <div className="space-y-3">
                      {[
                        { client: "Direct-Madiha LTD", logo: "DM", campaign: "UGC TikTok Dynamic Sweep", staff: "Lead Scribe", status: "RENDERING_UGC" },
                        { client: "Claire (Secure Invoice Register Acquisition)", logo: "CV", campaign: "NexoraX Resource Onboarding Video", staff: "CloudSync-02 Auto", status: "AWAITING_AUDIO" },
                        { client: "Helen (Client Resource Workflow Queue)", logo: "HR", campaign: "LinkedIn Outreach Sequence", staff: "Vanguard-Closer Node", status: "COMPLETED" },
                        { client: "Eleanor Vance (Licensed Portfolio)", logo: "EV", campaign: "High-CTR Avatar Calibration", staff: "Chronos Ledger", status: "LIVE_MONITORED" }
                      ].map((item, index) => (
                        <div key={index} className="p-3.5 bg-zinc-950/80 rounded-xl border border-white/5 flex flex-wrap justify-between items-center gap-3 font-mono text-xs">
                          <div className="flex items-center gap-2.5">
                            <span className="w-8 h-8 rounded-lg bg-purple-950/80 border border-purple-500/30 flex items-center justify-center text-[10px] font-black text-purple-300">
                              {item.logo}
                            </span>
                            <div className="text-left font-sans">
                              <h5 className="font-bold text-white uppercase tracking-tight text-[11px]">{item.campaign}</h5>
                              <p className="text-[10px] text-zinc-400">Client: <strong className="text-purple-300">{item.client}</strong></p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <span className="text-[9px] text-zinc-500 block">SLA Operator</span>
                              <span className="text-white text-[10px] block">{item.staff}</span>
                            </div>
                            <span className={`px-2 py-0.5 rounded text-[8px] font-mono font-black border ${
                              item.status === 'LIVE_MONITORED' ? 'bg-emerald-950/50 border-emerald-500/30 text-emerald-300' :
                              item.status === 'COMPLETED' ? 'bg-[#00e5ff]/25 border-cyan-500/30 text-[#00e5ff]' :
                              item.status === 'RENDERING_UGC' ? 'bg-purple-950/50 border-purple-500/30 text-purple-300 animate-pulse' :
                              'bg-amber-950/50 border-amber-500/30 text-amber-300'
                            }`}>
                              {item.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Right Column: Enterprise Standards & Remote Node Sync */}
                  <div className="bg-[#0f041d]/95 rounded-2xl border border-purple-500/20 p-5 space-y-4">
                    <h4 className="text-xs font-mono font-black text-amber-400 uppercase tracking-widest flex items-center gap-1.5 border-b border-purple-500/20 pb-2">
                      🛡 REMOTE NODE ENTERPRISE STANDARDS TRACKER
                    </h4>

                    {/* Pakistan Remote Node Info */}
                    <div className="space-y-3 font-mono text-xs">
                      <div className="p-3 bg-black/60 rounded-xl border border-white/5 space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-white font-sans font-bold">Staff Coordinator</span>
                          <span className="text-amber-400 bg-amber-950/50 px-2 py-0.2 rounded text-[9px] font-black uppercase animate-pulse">Pending-Migration</span>
                        </div>
                        <p className="text-[10.5px] text-zinc-400 font-sans">
                          Enterprise Standards file synchronized via <strong>'Pakistan_Node_Sync'</strong> command guidelines. Master sponsor file: NexoraX Digital Ltd (SC887095).
                        </p>
                      </div>

                      {/* Timeline stages */}
                      <div className="space-y-2 text-[10px]">
                        <div className="flex items-center justify-between border-b border-white/5 pb-1">
                          <span className="text-emerald-400">✔ Sponsorship License Active</span>
                          <span className="text-zinc-500">SC887095</span>
                        </div>
                        <div className="flex items-center justify-between border-b border-white/5 pb-1">
                          <span className="text-emerald-400">✔ Invitation to Join Drafted</span>
                          <span className="text-zinc-500">Node-Migrate</span>
                        </div>
                        <div className="flex items-center justify-between border-b border-white/5 pb-1">
                          <span className="text-emerald-400">✔ Proof Of Role Role-Necessity</span>
                          <span className="text-zinc-500">Amazonia Grid</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-amber-400 animate-pulse">◉ Home Office Compliance Audit</span>
                          <span className="text-zinc-300 font-bold">Day 11/15</span>
                        </div>
                      </div>

                      {/* Gavel protocol button */}
                      <button
                        onClick={() => {
                          triggerToast("Citing company growth impact, Institutional Mandate of Inquiry generated against delayed Sponsorship file Staff Coordinator.", "success");
                        }}
                        className="w-full mt-2 py-2 bg-gradient-to-r from-red-950/80 to-purple-950/80 border border-red-500/30 text-red-300 hover:text-white transition rounded-xl text-[9px] uppercase font-black tracking-widest cursor-pointer"
                      >
                        ⚡ TRIGGER GAVEL MANDATE OF INQUIRY
                      </button>

                      {/* Flagged Synchronized documents */}
                      <div className="pt-3 border-t border-purple-500/20 text-[9px] space-y-1">
                        <span className="text-zinc-500 block uppercase font-bold mb-1">Synchronized Files ('Pakistan_Node_Sync')</span>
                        <div className="flex justify-between items-center bg-black/40 p-1.5 rounded">
                          <span>Sponsor_Certificate_Communications Associate.pdf</span>
                          <span className="text-[#00e5ff]">READY</span>
                        </div>
                        <div className="flex justify-between items-center bg-black/40 p-1.5 rounded mt-1">
                          <span>Staff Coordinator_Compliance_Necessity_V2.pdf</span>
                          <span className="text-[#00e5ff]">READY</span>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          )}

          {/* === 12. SUPER ADMIN CONTROL === */}
          {activeTab === "superadmin" && role === "Super Admin" && (
            <div className="space-y-6 text-left">
              <div className="bg-gradient-to-r from-[#03152d] to-[#04010a] p-6 rounded-3xl border border-cyan-500/30 space-y-4">
                <div className="flex justify-between items-center border-b border-cyan-500/15 pb-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded bg-cyan-950 border border-cyan-500/30 text-[8px] font-mono font-black text-cyan-300 uppercase tracking-widest animate-pulse">
                        PLATFORM CORE OVERLORD
                      </span>
                      <span className="text-zinc-500 text-[10px] font-mono">ID: NODE-07 QUANTUM BILLING</span>
                    </div>
                    <h3 className="text-xl font-sans font-black text-white uppercase tracking-tight">
                      ⚡ SUPER PLATFORM SECURITY CONTROL
                    </h3>
                    <p className="text-zinc-400 text-xs">Configure ledger 3% Community Allocation divisions, verify SecurePayment settlement routing, and audit NexoraX Resource Library sync status.</p>
                  </div>
                  <Sliders className="text-[#00e5ff] animate-pulse" size={24} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left Column: Node-07 live Ledger calculator */}
                  <div className="lg:col-span-2 bg-black/60 rounded-2xl border border-cyan-500/10 p-5 space-y-4">
                    <div className="flex justify-between items-center border-b border-white/5 pb-2">
                      <h4 className="text-xs font-mono font-black text-cyan-400 uppercase tracking-wider flex items-center gap-1.5">
                        <Clock size={11} />
                        Node-07 Daily Gross Harvest Ticker
                      </h4>
                      <span className="text-[8px] font-mono text-zinc-500">LIVE COGNITIVE SYNAPSE</span>
                    </div>

                    <div className="p-3.5 bg-cyan-950/15 rounded-xl border border-cyan-500/10 text-xs space-y-3 font-sans">
                      <p className="text-cyan-300">
                        In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.
                      </p>
                      
                      <div className="space-y-2">
                        <label className="block text-[10.5px] text-zinc-400 font-mono font-bold uppercase">Insert Daily Gross Harvest (£):</label>
                        <div className="flex gap-2">
                          <input
                            type="number"
                            id="gross-harvest-input-calculator"
                            placeholder="e.g. 2820000"
                            className="flex-1 bg-zinc-950 border border-purple-500/30 rounded-xl p-2.5 text-white font-mono text-xs focus:outline-none focus:border-cyan-400"
                            defaultValue={2820000}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              const levy = val * 0.01;
                              const remainingAfterLevy = val - levy;
                              const taxGavel = remainingAfterLevy * 0.20;
                              const netSettlement = remainingAfterLevy - taxGavel;
                              const charitySplit = levy / 3.0;

                              const elLevy = document.getElementById("calc-levy");
                              const elTax = document.getElementById("calc-tax");
                              const elNet = document.getElementById("calc-net");
                              const elChar1 = document.getElementById("calc-charity-1");
                              const elChar2 = document.getElementById("calc-charity-2");
                              const elChar3 = document.getElementById("calc-charity-3");

                              if (elLevy) elLevy.innerText = "£" + levy.toLocaleString(undefined, { maximumFractionDigits: 2 });
                              if (elTax) elTax.innerText = "£" + taxGavel.toLocaleString(undefined, { maximumFractionDigits: 2 });
                              if (elNet) elNet.innerText = "£" + netSettlement.toLocaleString(undefined, { maximumFractionDigits: 2 });
                              if (elChar1) elChar1.innerText = "£" + charitySplit.toLocaleString(undefined, { maximumFractionDigits: 2 });
                              if (elChar2) elChar2.innerText = "£" + charitySplit.toLocaleString(undefined, { maximumFractionDigits: 2 });
                              if (elChar3) elChar3.innerText = "£" + charitySplit.toLocaleString(undefined, { maximumFractionDigits: 2 });
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              triggerToast("Ledger state successfully mirrored to the Google Drive NexoraX Resource Library via CloudSync-02.", "success");
                            }}
                            className="px-4 py-2 bg-cyan-400 hover:bg-cyan-500 text-black font-sans font-black text-[9px] uppercase tracking-wider rounded-xl transition cursor-pointer"
                          >
                            Mirror Ledger
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Calculated values layout */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 font-mono text-[11px] text-zinc-400 text-left">
                      <div className="p-3 bg-zinc-950/70 border border-white/5 rounded-xl space-y-1">
                        <span className="text-[8px] text-zinc-500 block uppercase font-bold">1% Perpetual 3% Community Allocation</span>
                        <strong className="text-[#00e5ff] text-md block font-black text-[13px]" id="calc-levy">£28,200.00</strong>
                        <span className="text-[8px] text-zinc-500 block">split 33.33% to Charities:</span>
                        <div className="text-[8.5px] text-purple-300 space-y-0.5 pt-1 border-t border-white/5 mt-1 leading-normal">
                          <p>Charity 1 (Dunfermline): <span id="calc-charity-1" className="text-white">£9,400.00</span></p>
                          <p>Charity 2 (Global): <span id="calc-charity-2" className="text-white">£9,400.00</span></p>
                          <p>Charity 3 (UK): <span id="calc-charity-3" className="text-white">£9,400.00</span></p>
                        </div>
                      </div>

                      <div className="p-3 bg-zinc-950/70 border border-white/5 rounded-xl space-y-1">
                        <span className="text-[8px] text-zinc-500 block uppercase font-bold">10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee)</span>
                        <strong className="text-purple-300 text-md block font-black text-[13px]" id="calc-tax">£558,360.00</strong>
                        <span className="text-[8.5px] text-zinc-500">Deducted from balance post-levy.</span>
                      </div>

                      <div className="p-3 bg-cyan-950/20 border border-cyan-500/20 rounded-xl space-y-1">
                        <span className="text-[8px] text-zinc-500 block uppercase font-bold">Net finance Settlement</span>
                        <strong className="text-emerald-400 text-md block font-black text-[13px]" id="calc-net">£2,233,440.00</strong>
                        <span className="text-[8px] text-zinc-500 block uppercase">Settles To SecurePayment Vault securely:</span>
                        <div className="text-[8.5px] text-white/80 space-y-0.5 pt-1 border-t border-white/5 mt-1 leading-normal">
                          <p>Sort Code: <strong>04-**-**</strong></p>
                          <p>Account: <strong>******15</strong></p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Platform compliance & secret info */}
                  <div className="bg-[#030d1b] rounded-2xl border border-cyan-500/20 p-5 space-y-4">
                    <h4 className="text-xs font-mono font-black text-cyan-400 uppercase tracking-widest border-b border-cyan-500/10 pb-2">
                      🔐 COMPLIANCE & CREDENTIALS
                    </h4>

                    <div className="space-y-4 font-mono text-[10px] text-zinc-300">
                      <div>
                        <span className="text-zinc-500 block font-bold uppercase">Secure Payment Provider Access registration Database</span>
                        <div className="bg-black/60 p-2.5 rounded-lg border border-white/5 space-y-1 mt-1 text-zinc-400">
                          <p>SDA Authority: <strong className="text-white">NI: NH******B</strong></p>
                          <p>License Identity: <strong className="text-white">SC887095 (NexoraX)</strong></p>
                        </div>
                      </div>

                      <div>
                        <span className="text-zinc-500 block font-bold uppercase">Google Drive NexoraX Resource Library Sync</span>
                        <div className="flex justify-between items-center bg-black/60 p-2.5 rounded-lg border border-white/5 mt-1">
                          <span className="text-emerald-400">✔ Sync active</span>
                          <span className="text-cyan-400 text-[9px] font-mono">SECURE_VAULT_SYNCED</span>
                        </div>
                      </div>

                      <div>
                        <span className="text-zinc-500 block font-bold uppercase">Resource resource Calibration</span>
                        <div className="bg-black/60 p-2.5 rounded-lg border border-white/5 space-y-1 mt-1 text-zinc-400">
                          <p>NexoraX Target: <span className="text-white">£5,100,000 Allocation</span></p>
                          <p>Access protocol code: <span className="text-[#00e5ff]">VIP_SINC_OS_NODE_09</span></p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* 5. BOTTOM CTA SECTION */}
      <div className="p-6 rounded-3xl bg-gradient-to-r from-cyan-950/30 to-[#180825]/40 border border-cyan-500/20 text-center space-y-2">
        <h3 className="text-md sm:text-lg font-sans font-black text-white uppercase tracking-wider">
          Your Brand. Your Campaigns. Powered by NexoraX.
        </h3>
        <p className="text-xs font-mono text-[#00e5ff] uppercase font-black tracking-widest leading-none">
          Create. Approve. Publish. Scale.
        </p>
      </div>
    </div>
  );
}
